import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  BookOpen, 
  Search,
  X,
  FileText, 
  ZoomIn, 
  ZoomOut,
  Maximize2,
  Minimize2,
  Eye,
  CheckCircle2,
  List,
  ChevronDown,
  Hash,
  Filter,
  Bookmark,
  BookmarkCheck,
  Play,
  Pause,
  Printer,
  Clock,
  ChevronRight,
  SlidersHorizontal,
  Trash2,
  Edit,
  Camera,
  Loader2,
  Bug,
  Edit3,
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from "@/lib/utils";
import { Document, Page, pdfjs } from 'react-pdf';
import { db, auth, storage } from '../../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, serverTimestamp, setDoc, collection, addDoc, getDocs, deleteDoc } from 'firebase/firestore';
import html2canvas from 'html2canvas';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const buildProgressScopeId = (school?: string | null, className?: string | null) => {
  const normalize = (value?: string | null, fallback = 'unassigned') => {
    const normalized = (value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return normalized || fallback;
  };

  return `resume__${normalize(school, 'all-schools')}__${normalize(className, 'all-classes')}`;
};

const getTrackablePages = (container: HTMLDivElement | null) => {
  if (!container) return [] as HTMLElement[];
  return Array.from(container.querySelectorAll<HTMLElement>('[data-progress-page="true"]'));
};

const getScrollMetrics = (container: HTMLDivElement | null) => {
  if (!container) {
    return { progressPercent: 0, currentPage: 0, totalPages: 0, scrollTop: 0 };
  }

  const { scrollTop, scrollHeight, clientHeight } = container;
  const pages = getTrackablePages(container);
  const totalPages = pages.length;

  // Method 1: Standard Scroll Height
  const method1_standard = Math.max(0, Math.min(100, (scrollTop / Math.max(scrollHeight - clientHeight, 1)) * 100));

  // Method 2: Page Bounding Rects (Visual position)
  let method2_visual = 0;
  if (totalPages > 0) {
    const containerRect = container.getBoundingClientRect();
    let closestIndex = 0;
    let minDist = Infinity;
    pages.forEach((page, idx) => {
      const dist = Math.abs(page.getBoundingClientRect().top - containerRect.top);
      if (dist < minDist) {
        minDist = dist;
        closestIndex = idx;
      }
    });
    // Add fractional progress based on how far we are through the current page
    const activePage = pages[closestIndex];
    const pageRect = activePage.getBoundingClientRect();
    const pageFraction = Math.max(0, Math.min(1, (containerRect.top - pageRect.top) / pageRect.height));
    method2_visual = Math.min(100, Math.max(0, ((closestIndex + pageFraction) / totalPages) * 100));
  }

  // Method 3: Container Bounding Box vs First/Last Child
  let method3_bounds = 0;
  if (container.firstElementChild && container.lastElementChild) {
    const firstTop = container.firstElementChild.getBoundingClientRect().top;
    const lastBottom = container.lastElementChild.getBoundingClientRect().bottom;
    const totalContentHeight = lastBottom - firstTop;
    const scrollOffset = container.getBoundingClientRect().top - firstTop;
    method3_bounds = Math.max(0, Math.min(100, (scrollOffset / Math.max(totalContentHeight - clientHeight, 1)) * 100));
  } else {
    method3_bounds = method1_standard;
  }

  // Method 4: Cumulative Offset Height
  let method4_cumulative = 0;
  let totalOffsetHeight = 0;
  let runningHeight = 0;
  Array.from(container.children).forEach((child: Element) => {
    totalOffsetHeight += (child as HTMLElement).offsetHeight || 0;
  });
  if (totalOffsetHeight > 0) {
    for (const child of Array.from(container.children)) {
      const h = (child as HTMLElement).offsetHeight || 0;
      if (runningHeight + h >= scrollTop) {
        const fraction = (scrollTop - runningHeight) / Math.max(h, 1);
        runningHeight += h * fraction;
        break;
      }
      runningHeight += h;
    }
    method4_cumulative = Math.max(0, Math.min(100, (runningHeight / Math.max(totalOffsetHeight - clientHeight, 1)) * 100));
  } else {
    method4_cumulative = method1_standard;
  }

  // Method 5: Offset Top Tracking
  let method5_offsetTop = 0;
  if (totalPages > 0) {
    let closestIndex = 0;
    let minDist = Infinity;
    pages.forEach((page, idx) => {
      const dist = Math.abs(page.offsetTop - scrollTop);
      if (dist < minDist) {
        minDist = dist;
        closestIndex = idx;
      }
    });
    method5_offsetTop = (closestIndex / Math.max(totalPages - 1, 1)) * 100;
  } else {
    method5_offsetTop = method1_standard;
  }

  // Gather all 5 methods
  let methods = [method1_standard, method2_visual, method3_bounds, method4_cumulative, method5_offsetTop];
  // Filter out any NaN/Infinity and clamp strictly to 0-100
  methods = methods.filter(m => Number.isFinite(m)).map(m => Math.max(0, Math.min(100, m)));

  // Find Consensus (Majority Cluster)
  let consensusPercent = method1_standard;
  if (methods.length > 0) {
    // Sort to find clusters
    methods.sort((a, b) => a - b);
    
    let bestCluster = [methods[0]];
    let bestClusterSize = 1;
    
    // Find the largest cluster of values within 5% of each other
    for (let i = 0; i < methods.length; i++) {
      const currentCluster = [methods[i]];
      for (let j = i + 1; j < methods.length; j++) {
        if (Math.abs(methods[j] - methods[i]) <= 5) {
          currentCluster.push(methods[j]);
        }
      }
      if (currentCluster.length > bestClusterSize) {
        bestClusterSize = currentCluster.length;
        bestCluster = currentCluster;
      }
    }
    
    // Average the majority cluster
    const clusterSum = bestCluster.reduce((sum, val) => sum + val, 0);
    consensusPercent = clusterSum / bestCluster.length;
  }

  const progressPercent = consensusPercent;

  // Derive current page purely from consensus percent
  const currentPage = totalPages > 0
    ? Math.min(totalPages, Math.max(progressPercent > 0 ? 1 : 0, Math.ceil((progressPercent / 100) * totalPages)))
    : 0;

  const metrics = { progressPercent, currentPage, totalPages, scrollTop };
  if (scrollTop > 0 && Math.random() < 0.05) {
    console.log('[DEBUG_RESUME] getScrollMetrics CONSENSUS calculated:', {
      methods: {
        standard: method1_standard.toFixed(1),
        visual: method2_visual.toFixed(1),
        bounds: method3_bounds.toFixed(1),
        cumulative: method4_cumulative.toFixed(1),
        offsetTop: method5_offsetTop.toFixed(1)
      },
      consensus: progressPercent.toFixed(1),
      metrics
    });
  }
  return metrics;
};

interface ReaderBookmark { id: string; label: string; scrollTop: number; topicTitle: string; subTopicTitle?: string; createdAt: string; }

interface ViewerProps {
  isEmbedded?: boolean;
  directPdfUrl?: string;
  onCloseDirect?: () => void;
}

const COURSE_PAGE_WIDTH = 816;
const COURSE_PAGE_HEIGHT = 1056;

const hasStoredContent = (data?: Record<string, any> | null) => {
  return Boolean(data?.referencePdfUrl || data?.outlineContent);
};

function useInView(options?: IntersectionObserverInit) {
  const [inView, setInView] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    
    // Stringify options to avoid infinite loop if options object identity changes
    const rootMargin = options?.rootMargin;
    const threshold = options?.threshold;
    
    const observer = new IntersectionObserver(([entry]) => {
      setInView(entry.isIntersecting);
    }, { rootMargin, threshold });

    observer.observe(element);
    return () => observer.disconnect();
  }, [options?.rootMargin, options?.threshold]);

  return { ref, inView };
}

const LazyPdfPage = ({ 
  pageNumber, 
  pageFitScale, 
  isQuickSearchSelecting, 
  quickSearchOverlayRef, 
  handleQuickSearchMouseDown, 
  handleQuickSearchMouseMove, 
  finishQuickSearchCapture,
  handleQuickSearchTouchEnd,
  isQuickSearchCapturing, 
  cancelQuickSearchCapture, 
  quickSearchRect 
}: any) => {
  const { ref, inView } = useInView({ rootMargin: '1200px 0px', threshold: 0 });
  const [actualHeight, setActualHeight] = useState<number>(COURSE_PAGE_HEIGHT);
  
  const fittedWidth = COURSE_PAGE_WIDTH * pageFitScale;
  const fittedHeight = actualHeight * pageFitScale;

  return (
    <div
      ref={ref}
      data-progress-page="true"
      className="course-reader-page-slot relative overflow-hidden"
      style={{ width: fittedWidth, height: fittedHeight, transition: 'height 0.3s ease-out' }}
    >
      {inView ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="absolute left-0 top-0"
          style={{ width: COURSE_PAGE_WIDTH, minHeight: actualHeight }}
        >
          <div
            className="course-reader-page bg-white border border-slate-300"
            style={{
              width: COURSE_PAGE_WIDTH,
              minHeight: COURSE_PAGE_HEIGHT,
              transform: `scale(${pageFitScale})`,
              transformOrigin: 'top left',
              boxShadow: '0 8px 32px 0 rgba(0,0,0,0.22), 0 1.5px 4px 0 rgba(0,0,0,0.10)',
            }}
          >
             <Page
                pageNumber={pageNumber}
                width={COURSE_PAGE_WIDTH}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                onLoadSuccess={(page: any) => {
                  try {
                    const viewport = page.getViewport({ scale: 1 });
                    const ratio = COURSE_PAGE_WIDTH / viewport.width;
                    const calculatedHeight = viewport.height * ratio;
                    setActualHeight(calculatedHeight);
                  } catch (e) {
                    console.error("Error calculating page height", e);
                  }
                }}
                loading={
                  <div className="h-full w-full animate-pulse bg-gradient-to-b from-slate-100 via-white to-slate-50 flex flex-col gap-4 p-16" style={{ minHeight: COURSE_PAGE_HEIGHT }}>
                    <div className="h-4 bg-slate-200 rounded-full w-2/3" />
                    <div className="h-3 bg-slate-100 rounded-full w-full" />
                    <div className="h-3 bg-slate-100 rounded-full w-5/6" />
                    <div className="h-3 bg-slate-100 rounded-full w-full" />
                    <div className="h-3 bg-slate-100 rounded-full w-3/4 mt-4" />
                    <div className="h-3 bg-slate-100 rounded-full w-full" />
                  </div>
                }
             />
          </div>
          {isQuickSearchSelecting && (
            <div
              ref={quickSearchOverlayRef}
              data-html2canvas-ignore="true"
              className="absolute inset-0 z-[260] cursor-crosshair bg-slate-950/20 touch-none"
              onMouseDown={handleQuickSearchMouseDown}
              onMouseMove={handleQuickSearchMouseMove}
              onMouseUp={finishQuickSearchCapture}
              onTouchStart={handleQuickSearchMouseDown}
              onTouchMove={handleQuickSearchMouseMove}
              onTouchEnd={handleQuickSearchTouchEnd}
            >
              {/* Instruction card — fixed so always fully visible on all screen sizes */}
              <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[300] rounded-xl border-2 border-blue-400 bg-blue-950 px-5 py-3 text-center shadow-2xl max-w-[90vw] pointer-events-auto">
                <div className="text-[11px] font-black uppercase tracking-widest text-white">
                  {isQuickSearchCapturing ? '⏳ Capturing…' : '✏️ Draw a box around the content'}
                </div>
                <button
                  onClick={(event) => { event.stopPropagation(); cancelQuickSearchCapture?.(); }}
                  onTouchStart={(event) => event.stopPropagation()}
                  onTouchEnd={(event) => { event.stopPropagation(); cancelQuickSearchCapture?.(); }}
                  className="mt-1.5 text-[10px] font-bold uppercase tracking-widest text-blue-300 hover:text-white border border-blue-500/40 hover:border-blue-400 px-3 py-0.5 rounded-full transition-colors"
                >
                  Cancel
                </button>
              </div>
              {quickSearchRect && (
                <div
                  className="absolute border-2 border-blue-400 bg-blue-400/15 pointer-events-none"
                  style={{
                    left: quickSearchRect.x,
                    top: quickSearchRect.y,
                    width: quickSearchRect.width,
                    height: quickSearchRect.height,
                  }}
                />
              )}
            </div>
          )}
        </motion.div>
      ) : (
        <div className="absolute left-0 top-0" style={{ width: COURSE_PAGE_WIDTH, height: actualHeight }}>
          <div className="course-reader-page bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800" style={{ width: COURSE_PAGE_WIDTH, height: actualHeight, transform: `scale(${pageFitScale})`, transformOrigin: 'top left' }}>
             <div className="h-full w-full flex items-center justify-center">
                <Loader2 className="h-6 w-6 text-slate-300 dark:text-slate-700 animate-spin" />
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SyllabusDocumentSection = ({
  file,
  zoom,
  title,
  pageFitScale = 1,
  isQuickSearchSelecting = false,
  quickSearchOverlayRef,
  handleQuickSearchMouseDown,
  handleQuickSearchMouseMove,
  finishQuickSearchCapture,
  handleQuickSearchTouchEnd,
  isQuickSearchCapturing = false,
  cancelQuickSearchCapture,
  quickSearchRect,
  onDocumentLoaded
}: {
  file: string,
  zoom: number,
  title?: string,
  pageFitScale?: number,
  isQuickSearchSelecting?: boolean,
  quickSearchOverlayRef?: React.RefObject<HTMLDivElement | null>,
  handleQuickSearchMouseDown?: (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => void,
  handleQuickSearchMouseMove?: (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => void,
  finishQuickSearchCapture?: (event?: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => void,
  handleQuickSearchTouchEnd?: (event: React.TouchEvent<HTMLDivElement>) => void,
  isQuickSearchCapturing?: boolean,
  cancelQuickSearchCapture?: () => void,
  quickSearchRect?: { x: number; y: number; width: number; height: number } | null,
  onDocumentLoaded?: () => void,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [retryKey, setRetryKey] = useState<number>(0);
  const fittedWidth = COURSE_PAGE_WIDTH * pageFitScale;
  const fittedHeight = COURSE_PAGE_HEIGHT * pageFitScale;

  if (loadError) {
    return (
      <div
        data-progress-page="true"
        className="course-reader-page-slot flex flex-col items-center justify-center gap-4 bg-slate-100 dark:bg-slate-900 border border-dashed border-slate-300 dark:border-slate-700 rounded-2xl shadow-inner"
        style={{ width: fittedWidth, minHeight: fittedHeight }}
      >
        <Loader2 className="h-6 w-6 text-slate-400 animate-spin" />
        <div className="text-center space-y-1">
          <p className="text-[11px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Failed to load PDF</p>
          <p className="text-[10px] text-slate-400 dark:text-slate-500">Network error or the file is unavailable</p>
        </div>
        <button
          onClick={() => { setLoadError(false); setNumPages(0); setRetryKey(k => k + 1); }}
          className="mt-1 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-black uppercase tracking-widest transition-all active:scale-95"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-12 w-full">
      {title && (
        <div className="flex items-center gap-2 px-6 py-2 bg-slate-900 shadow-xl rounded-full mb-4">
           <FileText className="h-4 w-4 text-white/50" />
           <span className="text-[10px] font-black uppercase tracking-[0.3em] text-white underline decoration-white/20 underline-offset-4">{title}</span>
        </div>
      )}
      <Document
        key={retryKey}
        file={file}
        onLoadSuccess={({ numPages }) => { setLoadError(false); setNumPages(numPages); onDocumentLoaded?.(); }}
        onLoadError={() => setLoadError(true)}
        className="flex flex-col items-center gap-40"
        loading={
          <div className="flex flex-col items-center gap-40">
            {[0,1,2].map(i => (
              <div
                key={i}
                data-progress-page="true"
                className="course-reader-page-slot relative overflow-hidden"
                style={{ width: fittedWidth, height: fittedHeight }}
              >
                <div
                  className="absolute left-0 top-0 bg-white border border-slate-200 rounded"
                  style={{
                    width: COURSE_PAGE_WIDTH,
                    height: COURSE_PAGE_HEIGHT,
                    transform: `scale(${pageFitScale})`,
                    transformOrigin: 'top left',
                    boxShadow: '0 8px 32px 0 rgba(0,0,0,0.18), 0 1.5px 4px 0 rgba(0,0,0,0.10)',
                  }}
                >
                  <div className="h-full w-full animate-pulse bg-gradient-to-b from-slate-100 via-white to-slate-50 flex flex-col gap-4 p-16">
                    <div className="h-4 bg-slate-200 rounded-full w-2/3" />
                    <div className="h-3 bg-slate-100 rounded-full w-full" />
                    <div className="h-3 bg-slate-100 rounded-full w-5/6" />
                    <div className="h-3 bg-slate-100 rounded-full w-full" />
                    <div className="h-3 bg-slate-100 rounded-full w-3/4 mt-4" />
                    <div className="h-3 bg-slate-100 rounded-full w-full" />
                    <div className="h-3 bg-slate-100 rounded-full w-5/6" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        }
      >
        {Array.from(new Array(numPages), (el, index) => (
          <LazyPdfPage
            key={`page_${index + 1}`}
            pageNumber={index + 1}
            pageFitScale={pageFitScale}
            isQuickSearchSelecting={isQuickSearchSelecting}
            quickSearchOverlayRef={quickSearchOverlayRef}
            handleQuickSearchMouseDown={handleQuickSearchMouseDown}
            handleQuickSearchMouseMove={handleQuickSearchMouseMove}
            finishQuickSearchCapture={finishQuickSearchCapture}
            handleQuickSearchTouchEnd={handleQuickSearchTouchEnd}
            isQuickSearchCapturing={isQuickSearchCapturing}
            cancelQuickSearchCapture={cancelQuickSearchCapture}
            quickSearchRect={quickSearchRect}
          />
        ))}
      </Document>
    </div>
  );
};

const compressImage = (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const MAX_WIDTH = 1600;
          const MAX_HEIGHT = 1600;
          let { width, height } = img;
          if (width > height) {
            if (width > MAX_WIDTH) { height *= MAX_WIDTH / width; width = MAX_WIDTH; }
          } else {
            if (height > MAX_HEIGHT) { width *= MAX_HEIGHT / height; height = MAX_HEIGHT; }
          }
          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            blob => blob ? resolve(blob) : reject(new Error('Canvas to Blob failed')),
            'image/jpeg',
            0.7
          );
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = error => reject(error);
      img.src = event.target?.result as string;
    };
    reader.onerror = error => reject(error);
  });
};

const SubTopicContentViewer = ({ isEmbedded = false, directPdfUrl, onCloseDirect }: ViewerProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const getInitialValue = (key: string, fallback: any) => {
    if (location.state && (location.state as any)[key] !== undefined) {
      return (location.state as any)[key];
    }
    // If location.state is set but the key is missing, use the fallback
    // instead of localStorage. This prevents stale "full subject view" state
    // (e.g. isFullSubjectView=true, syllabusData, subContentMap) from leaking
    // in when the user navigates from the creator/structure editor with a
    // single-item "view content" payload. localStorage is only used as a
    // fallback when there is no state at all (e.g. a hard refresh).
    if (location.state) {
      return fallback;
    }
    try {
      const saved = localStorage.getItem(`vochak_viewer_${key}`);
      if (saved !== null) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error(e);
    }
    return fallback;
  };

  const routePdfUrl = getInitialValue('directPdfUrl', null);
  const resolvedPdfUrl = directPdfUrl || routePdfUrl || null;
  const routeHtmlContent = getInitialValue('htmlContent', null);
  const contentTitle = getInitialValue('contentTitle', null);
  const isFullSubjectView = getInitialValue('isFullSubjectView', false);
  const subjectId = getInitialValue('subjectId', null);
  const subjectName = getInitialValue('subjectName', null) || contentTitle || null;
  const selectedSchool = getInitialValue('selectedSchool', '');
  const selectedClass = getInitialValue('selectedClass', '');
  const progressScopeId = getInitialValue('progressScopeId', '') || buildProgressScopeId(selectedSchool, selectedClass);
  const fullSyllabus = getInitialValue('syllabusData', null);
  const fullSubContentMap = getInitialValue('subContentMap', null);
  const forceProgressReload = getInitialValue('forceProgressReload', null);
  const docType = getInitialValue('docType', null) as string | null;
  const topicId = getInitialValue('topicId', null) as string | null;
  const subTopicId = getInitialValue('subTopicId', null) as string | null;
  const subTopicTitle = getInitialValue('subTopicTitle', null) as string | null;
  const isFirebaseSubject = getInitialValue('isFirebaseSubject', false) as boolean;
  const initialItems = getInitialValue('items', null) as any[] | null;
  const initialActiveItemId = getInitialValue('activeItemId', null) as string | null;

  // Final fallback for the header title: subject name, then content title, then the
  // bare sub-topic title that the editor passes when navigating into the viewer.
  const resolvedHeaderTitle = subjectName || contentTitle || subTopicTitle || 'Curriculum';

  const getCanonicalSubTopicContent = React.useCallback((topic: any, subTopic: any, subIndex: number) => {
    if (!fullSubContentMap) return null;
    const subTopicData = fullSubContentMap[`${topic.id}_${subTopic.id}`];
    if (hasStoredContent(subTopicData)) return subTopicData;

    // Legacy fallback: topic-level content belongs to the first subtopic once a topic is subdivided.
    if (subIndex === 0) {
      const topicData = fullSubContentMap[`${topic.id}_main`];
      if (hasStoredContent(topicData)) return topicData;
    }

    return subTopicData;
  }, [fullSubContentMap]);

  React.useEffect(() => {
    if (forceProgressReload) {
      try { localStorage.removeItem('vochak_viewer_forceProgressReload'); } catch (_) {}
    }
  }, []);
  const fromPath = getInitialValue('fromPath', null);
  const fromState = getInitialValue('fromState', null);
  const quickSearchRestore = getInitialValue('quickSearchRestore', null);
  const openedFromCreatorStructure =
    ((location.state as any)?.fromPath || fromPath) === '/dashboard/lessons/course/structure';
  const resumeProgressEnabled = isFullSubjectView && !openedFromCreatorStructure;

  const [pdfUrl, setPdfUrl] = useState<string | null>(resolvedPdfUrl);
  const [htmlContent, setHtmlContent] = useState<string | null>(routeHtmlContent || null);
  const [items, setItems] = useState<any[]>(initialItems || []);
  const [activeItemId, setActiveItemId] = useState<string | null>(initialActiveItemId || null);
  const [numPages, setNumPages] = useState<number>(0);
  const [zoom, setZoom] = useState(1.0);
  const [isFullscreenMode, setIsFullscreenMode] = useState(false);
  const [eyeCareStrength, setEyeCareStrength] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [completedAt, setCompletedAt] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const quickSearchOverlayRef = useRef<HTMLDivElement | null>(null);
  const [readerPaneWidth, setReaderPaneWidth] = useState(0);
  const saveProgressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedProgressRef = useRef({ progressPercent: 0, currentPage: 0, totalPages: 0, scrollTop: 0 });
  const latestProgressMetricsRef = useRef({ progressPercent: 0, currentPage: 0, totalPages: 0, scrollTop: 0 });
  const hasUnsavedProgressRef = useRef(false);
  const [restoreTarget, setRestoreTarget] = useState<{ progressPercent: number; currentPage: number; scrollTop: number; ready: boolean }>({ progressPercent: 0, currentPage: 0, scrollTop: 0, ready: false });
  const [waitingForReset, setWaitingForReset] = useState(!!forceProgressReload);
  const hasRestoredProgressRef = useRef(false);
  React.useEffect(() => { isResizingNotePaneRef.current = false; }, []);

  React.useEffect(() => {
    if (location.state) {
      const persistKeys = [
        'directPdfUrl',
        'htmlContent',
        'contentTitle',
        'subjectId',
        'subjectName',
        'selectedSchool',
        'selectedClass',
        'forceProgressReload',
        'fromPath',
        'fromState'
      ];
      persistKeys.forEach(key => {
        const val = (location.state as any)[key];
        if (val !== undefined) {
          try {
            localStorage.setItem(`vochak_viewer_${key}`, JSON.stringify(val));
          } catch (e) {
            console.error('Failed to save to localStorage', e);
          }
        }
      });

      // View-mode keys: explicitly set OR clear. This is what stops the
      // "view content shows the whole syllabus" bug — when the user navigates
      // from the creator/structure editor with a single-item payload (no
      // isFullSubjectView), we wipe the stale full-subject-view snapshot so a
      // subsequent refresh does not resurrect it.
      const viewModeKeys = ['isFullSubjectView', 'syllabusData', 'subContentMap', 'progressScopeId'];
      viewModeKeys.forEach(key => {
        const val = (location.state as any)[key];
        try {
          if (val === undefined || val === null) {
            localStorage.removeItem(`vochak_viewer_${key}`);
          } else {
            localStorage.setItem(`vochak_viewer_${key}`, JSON.stringify(val));
          }
        } catch (e) {
          console.error('Failed to sync view-mode localStorage', e);
        }
      });
    }
  }, [location.state]);

  const [showToolsPanel, setShowToolsPanel] = useState(false);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [debugData, setDebugData] = useState('');
  const [activeToolSection, setActiveToolSection] = useState<string | null>('navigation');
  
  // Track loaded PDFs to prevent progress bar jumps
  const [loadedPdfsCount, setLoadedPdfsCount] = useState(0);
  const totalExpectedPdfs = React.useMemo(() => {
    if (!isFullSubjectView || !fullSyllabus || !fullSubContentMap) return pdfUrl ? 1 : 0;
    let count = 0;
    if (fullSyllabus.referencePdfUrl) count++;
    fullSyllabus.topics?.forEach((topic: any) => {
      if (topic.subTopics?.length) {
        topic.subTopics.forEach((sub: any, subIndex: number) => {
          const data = getCanonicalSubTopicContent(topic, sub, subIndex);
          if (data?.referencePdfUrl) count++;
        });
      } else {
        const data = fullSubContentMap[`${topic.id}_main`];
        if (data?.referencePdfUrl) count++;
      }
    });
    return count;
  }, [isFullSubjectView, fullSyllabus, fullSubContentMap, pdfUrl, getCanonicalSubTopicContent]);
  const isFullyLoaded = (loadedPdfsCount >= totalExpectedPdfs);
  const isFullyLoadedRef = React.useRef(isFullyLoaded);
  React.useEffect(() => {
    isFullyLoadedRef.current = isFullyLoaded;
  }, [isFullyLoaded]);
  const [topicFilter, setTopicFilter] = useState<string>('all');
  const [jumpToInput, setJumpToInput] = useState('');
  const [totalPageCount, setTotalPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPdfPage, setCurrentPdfPage] = useState(1);
  const [bookmarks, setBookmarks] = useState<ReaderBookmark[]>([]);
  const [isSavingBookmark, setIsSavingBookmark] = useState(false);
  const [notes, setNotes] = useState<Record<string | number, string>>({});
  const notesRef = React.useRef(notes);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [noteScope, setNoteScope] = useState<'page' | 'subject'>('page');
  const [noteMode, setNoteMode] = useState<'text' | 'photo'>('text');
  const [isEditingPhotoNotes, setIsEditingPhotoNotes] = useState(false);
  const [uploadingPhotoNoteKey, setUploadingPhotoNoteKey] = useState<string | number | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [showNoteTakerSplit, setShowNoteTakerSplit] = useState(false);
  const [notePaneWidth, setNotePaneWidth] = useState<'normal' | 'wide'>('normal');
  const [noteTakerFullscreen, setNoteTakerFullscreen] = useState(false);
  const [showNotesIndex, setShowNotesIndex] = useState(false);
  const [lockedNotePage, setLockedNotePage] = useState<number | null>(null);
  const [isQuickSearchSelecting, setIsQuickSearchSelecting] = useState(false);
  const [quickSearchStart, setQuickSearchStart] = useState<{ x: number; y: number } | null>(null);
  const [quickSearchRect, setQuickSearchRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [isQuickSearchCapturing, setIsQuickSearchCapturing] = useState(false);
  const isResizingNotePaneRef = useRef(false);
  const pendingNotePaneRestoreRef = useRef<{ scrollTop: number; page: number } | null>(null);
  const [mobileContainerWidth, setMobileContainerWidth] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 816);
  useEffect(() => {
    const onResize = () => setMobileContainerWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const mobileFitScale = Math.min(1, (mobileContainerWidth - 24) / COURSE_PAGE_WIDTH);
  const coursePageFitScale = showNoteTakerSplit && readerPaneWidth > 0
    ? Math.max(0.45, Math.min(1, (readerPaneWidth - 128) / COURSE_PAGE_WIDTH))
    : mobileFitScale;
  const editorRef = useRef<HTMLDivElement>(null);
  const replacePhotoUploadRef = useRef(false);
  const pendingPhotoUploadKeyRef = useRef<string | number | null>(null);
  const activePage = lockedNotePage ?? (pdfUrl ? currentPdfPage : (currentPage || 1));
  const activeNoteKey = noteScope === 'subject' ? 'subject_global' : activePage;
  const lastActiveNoteKeyRef = useRef<string | number>(activeNoteKey);

  const getPhotoNoteKey = React.useCallback((key: string | number) => `${key}_photos`, []);
  const isPhotoNoteContent = React.useCallback((value?: string) => Boolean(value?.startsWith('image:') || value?.startsWith('images:')), []);
  const getPhotoNoteContent = React.useCallback((key: string | number, sourceNotes = notesRef.current) => {
    const photoKey = getPhotoNoteKey(key);
    const photoValue = sourceNotes[photoKey];
    if (isPhotoNoteContent(photoValue)) return photoValue;

    const legacyValue = sourceNotes[key];
    return isPhotoNoteContent(legacyValue) ? legacyValue : '';
  }, [getPhotoNoteKey, isPhotoNoteContent]);

  const debugPhotoNotes = React.useCallback((label: string, details: Record<string, unknown> = {}) => {
    const currentNotes = notesRef.current;
    const currentActiveKey = noteScope === 'subject' ? 'subject_global' : (lockedNotePage ?? (pdfUrl ? currentPdfPage : (currentPage || 1)));
    const currentPhotoKey = getPhotoNoteKey(currentActiveKey);
    const allNoteKeys = Object.keys(currentNotes);
    const photoKeys = allNoteKeys.filter(key => key.endsWith('_photos') || isPhotoNoteContent(currentNotes[key]));

    console.groupCollapsed(`[VOCHAK PHOTO NOTE DEBUG] ${label}`);
    console.log('details', details);
    console.log('viewer state', {
      noteMode,
      noteScope,
      isUploadingImage,
      isEditingPhotoNotes,
      activeNoteKey: currentActiveKey,
      activePhotoKey: currentPhotoKey,
      lockedNotePage,
      uploadingPhotoNoteKey,
      currentPage,
      currentPdfPage,
      pdfUrlPresent: Boolean(pdfUrl),
      subjectId,
      authUid: auth.currentUser?.uid || null,
    });
    console.log('active text note', {
      key: currentActiveKey,
      rawValue: currentNotes[currentActiveKey],
      isPhotoLegacyValue: isPhotoNoteContent(currentNotes[currentActiveKey]),
    });
    console.log('active photo note', {
      key: currentPhotoKey,
      rawValue: currentNotes[currentPhotoKey],
      resolvedValue: getPhotoNoteContent(currentActiveKey, currentNotes),
      resolvedUrls: getPhotoNoteContent(currentActiveKey, currentNotes).replace(/^images?:/, '').split('||').filter(Boolean),
    });
    console.log('all note keys', allNoteKeys);
    console.log('photo-like keys', photoKeys);
    console.log('all notes snapshot', currentNotes);
    console.trace('photo note trace');
    console.groupEnd();
  }, [
    currentPage,
    currentPdfPage,
    getPhotoNoteContent,
    getPhotoNoteKey,
    isEditingPhotoNotes,
    isPhotoNoteContent,
    isUploadingImage,
    lockedNotePage,
    noteMode,
    noteScope,
    pdfUrl,
    subjectId,
    uploadingPhotoNoteKey,
  ]);

  const lastSelectionRangeRef = useRef<Range | null>(null);
  const [activeNoteFormats, setActiveNoteFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikeThrough: false,
    insertUnorderedList: false,
    insertOrderedList: false,
    justifyLeft: false,
    justifyCenter: false,
    justifyRight: false,
  });
  const [activeNoteTextColor, setActiveNoteTextColor] = useState('#ffffff');
  const [activeNoteHighlightColor, setActiveNoteHighlightColor] = useState('transparent');
  const [activeNoteFontSize, setActiveNoteFontSize] = useState('3');

  const normalizeCssColor = (value: string) => {
    if (!value) return '';
    const trimmed = value.trim().toLowerCase();
    if (trimmed === 'transparent' || trimmed === 'rgba(0, 0, 0, 0)') return 'transparent';
    if (trimmed.startsWith('#')) {
      return trimmed.length === 4
        ? `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`
        : trimmed;
    }

    const rgb = trimmed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!rgb) return trimmed;
    return `#${[rgb[1], rgb[2], rgb[3]].map(part => Number(part).toString(16).padStart(2, '0')).join('')}`;
  };

  const updateNoteFormatState = React.useCallback(() => {
    if (!editorRef.current || isPhotoNoteContent(notes[activeNoteKey])) return;
    const selection = window.getSelection();
    const hasEditorSelection = selection && selection.rangeCount > 0 && editorRef.current.contains(selection.getRangeAt(0).commonAncestorContainer);
    if (!hasEditorSelection && document.activeElement !== editorRef.current) return;

    const query = (command: string) => {
      try {
        return Boolean(document.queryCommandState(command));
      } catch {
        return false;
      }
    };

    setActiveNoteFormats({
      bold: query('bold'),
      italic: query('italic'),
      underline: query('underline'),
      strikeThrough: query('strikeThrough'),
      insertUnorderedList: query('insertUnorderedList'),
      insertOrderedList: query('insertOrderedList'),
      justifyLeft: query('justifyLeft'),
      justifyCenter: query('justifyCenter'),
      justifyRight: query('justifyRight'),
    });

    const queryValue = (command: string) => {
      try {
        return document.queryCommandValue(command);
      } catch {
        return '';
      }
    };

    setActiveNoteTextColor(normalizeCssColor(queryValue('foreColor')) || '#ffffff');
    setActiveNoteHighlightColor(normalizeCssColor(queryValue('backColor')) || 'transparent');
    setActiveNoteFontSize(queryValue('fontSize') || '3');
  }, [activeNoteKey, isPhotoNoteContent, notes]);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
        lastSelectionRangeRef.current = range.cloneRange();
        updateNoteFormatState();
      }
    }
  };

  const restoreSelection = () => {
    if (lastSelectionRangeRef.current) {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(lastSelectionRangeRef.current.cloneRange());
      }
    }
  };

  React.useEffect(() => {
    if (!showNoteTakerSplit) return;
    document.addEventListener('selectionchange', updateNoteFormatState);
    return () => document.removeEventListener('selectionchange', updateNoteFormatState);
  }, [showNoteTakerSplit, updateNoteFormatState]);

  React.useEffect(() => {
    if (!isFullSubjectView || !scrollContainerRef.current) return;

    const element = scrollContainerRef.current;
    const updateWidth = () => setReaderPaneWidth(element.clientWidth);
    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, [isFullSubjectView, showNoteTakerSplit, notePaneWidth]);

  const [containerReady, setContainerReady] = useState(false);
  const [isRestoringScroll, setIsRestoringScroll] = useState(!!quickSearchRestore);
  const [isRestoringProgress, setIsRestoringProgress] = useState(
    // Always start true for full subject view — we only turn it off once the restore
    // sequence completes (or confirms there's nothing to restore).
    // This prevents any content flash before the stable-layout detector runs.
    resumeProgressEnabled
  );

  const scrollRefCallback = React.useCallback((node: HTMLDivElement | null) => {
    scrollContainerRef.current = node;
    if (node) setContainerReady(true);
  }, []);

  React.useEffect(() => {
    try { localStorage.removeItem('vochak_viewer_quickSearchRestore'); } catch (_) {}
    if (!quickSearchRestore) {
      setIsRestoringScroll(false);
      return;
    }
    if (!containerReady || !scrollContainerRef.current) return;
    const restore = quickSearchRestore as { scrollTop?: number; currentPage?: number };
    
    if (typeof restore.currentPage === 'number') {
      if (pdfUrl) {
        setCurrentPdfPage(restore.currentPage);
      } else {
        setCurrentPage(restore.currentPage);
      }
    }

    const container = scrollContainerRef.current;
    let attempts = 0;
    const attemptScrollRestore = () => {
      if (!container || typeof restore.scrollTop !== 'number') return true;
      const maxScroll = Math.max(container.scrollHeight - container.clientHeight, 0);
      if (maxScroll >= restore.scrollTop || attempts > 20) {
        container.scrollTop = restore.scrollTop;
        return true;
      }
      attempts++;
      return false;
    };

    if (attemptScrollRestore()) {
      setIsRestoringScroll(false);
      return;
    }

    const observer = new MutationObserver(() => {
      if (attemptScrollRestore()) {
        setIsRestoringScroll(false);
        observer.disconnect();
      }
    });
    observer.observe(container, { childList: true, subtree: true });

    const fallbackTimer = window.setTimeout(() => {
      attemptScrollRestore();
      setIsRestoringScroll(false);
      observer.disconnect();
    }, 4000);

    return () => {
      observer.disconnect();
      window.clearTimeout(fallbackTimer);
    };
  }, [quickSearchRestore, pdfUrl, containerReady]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const uploadNoteKey = pendingPhotoUploadKeyRef.current ?? activeNoteKey;
    const activePhotoKey = getPhotoNoteKey(uploadNoteKey);
    const shouldReplacePhotos = replacePhotoUploadRef.current;

    debugPhotoNotes('file input changed', {
      hasFile: Boolean(file),
      uploadNoteKey,
      activePhotoKey,
      shouldReplacePhotos,
      selectedFile: file ? {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified,
      } : null,
    });

    setNoteMode('photo');
    setIsEditingPhotoNotes(false);
    setUploadingPhotoNoteKey(uploadNoteKey);

    if (typeof uploadNoteKey === 'number' && Number.isFinite(uploadNoteKey)) {
      setLockedNotePage(uploadNoteKey);
    }

    if (!file) {
      debugPhotoNotes('file input closed without a file', { uploadNoteKey, activePhotoKey });
      setUploadingPhotoNoteKey(null);
      pendingPhotoUploadKeyRef.current = null;
      replacePhotoUploadRef.current = false;
      return;
    }

    setIsUploadingImage(true);
    try {
      const uid = auth.currentUser?.uid;
      debugPhotoNotes('upload starting', {
        uploadNoteKey,
        activePhotoKey,
        storagePathSubjectId: subjectId,
        uid,
      });

      if (!uid) {
        debugPhotoNotes('upload blocked: missing auth user', { uploadNoteKey, activePhotoKey });
        toast.error('Not authenticated');
        return;
      }
      
      let uploadBlob: Blob = file;
      try {
        uploadBlob = await compressImage(file);
        debugPhotoNotes('image compression succeeded', {
          originalSize: file.size,
          compressedSize: uploadBlob.size,
          uploadBlobType: uploadBlob.type,
        });
      } catch (compressErr) {
        console.warn('Image compression failed, falling back to raw file:', compressErr);
        debugPhotoNotes('image compression failed; using raw file', {
          error: compressErr,
          originalSize: file.size,
          originalType: file.type,
        });
        uploadBlob = file;
      }
      
      const storagePath = `teaching_workspaces/${uid}/notes/${subjectId || 'unknown_subject'}_key_${activePhotoKey}_${Date.now()}_img`;
      debugPhotoNotes('firebase storage upload about to run', {
        storagePath,
        uploadBlobSize: uploadBlob.size,
        uploadBlobType: uploadBlob.type,
      });
      const storageRef = ref(storage, storagePath);
      const snapshot = await uploadBytes(storageRef, uploadBlob);
      debugPhotoNotes('firebase storage upload finished', {
        fullPath: snapshot.ref.fullPath,
        bucket: snapshot.ref.bucket,
        contentType: snapshot.metadata?.contentType,
        size: snapshot.metadata?.size,
      });
      const downloadUrl = await getDownloadURL(snapshot.ref);
      debugPhotoNotes('download url resolved', {
        downloadUrl,
        uploadNoteKey,
        activePhotoKey,
      });

      const latestNotes = notesRef.current;
      const existingContent = shouldReplacePhotos ? '' : getPhotoNoteContent(uploadNoteKey, latestNotes);
      let newContent = `images:${downloadUrl}`;
      if (existingContent.startsWith('images:')) {
        newContent = `${existingContent}||${downloadUrl}`;
      } else if (existingContent.startsWith('image:')) {
        newContent = `images:${existingContent.substring(6)}||${downloadUrl}`;
      }

      debugPhotoNotes('computed new photo note content', {
        uploadNoteKey,
        activePhotoKey,
        shouldReplacePhotos,
        existingContent,
        newContent,
        newUrls: newContent.replace(/^images?:/, '').split('||').filter(Boolean),
      });

      setNotes(prev => ({ ...prev, [activePhotoKey]: newContent }));
      notesRef.current = { ...latestNotes, [activePhotoKey]: newContent };
      debugPhotoNotes('local notes state queued', {
        uploadNoteKey,
        activePhotoKey,
        newContent,
      });

      if (subjectId && auth.currentUser) {
        const uid2 = auth.currentUser.uid;
        const finalNotes = { ...latestNotes, [activePhotoKey]: newContent };
        debugPhotoNotes('firestore setDoc starting', {
          firestorePath: `teaching_workspaces/${uid2}/courses/${subjectId}/notes/pages_notes`,
          finalNoteKeys: Object.keys(finalNotes),
          finalPhotoValue: finalNotes[activePhotoKey],
        });
        await setDoc(doc(db, `teaching_workspaces/${uid2}/courses/${subjectId}/notes`, 'pages_notes'), {
          notes: finalNotes,
          updatedAt: new Date().toISOString()
        }, { merge: true });
        debugPhotoNotes('firestore setDoc finished', {
          firestorePath: `teaching_workspaces/${uid2}/courses/${subjectId}/notes/pages_notes`,
          activePhotoKey,
        });
      } else {
        debugPhotoNotes('firestore save skipped', {
          hasSubjectId: Boolean(subjectId),
          hasAuthUser: Boolean(auth.currentUser),
        });
      }

      setNoteMode('photo');
      setIsEditingPhotoNotes(false);
      debugPhotoNotes('upload success: viewer should show photo now', {
        uploadNoteKey,
        activePhotoKey,
        newContent,
      });
      toast.success('Handwritten photo note saved successfully');
    } catch (err: any) {
      console.error('Error uploading image note:', err);
      debugPhotoNotes('upload failed and viewer will stay on photo empty state', {
        uploadNoteKey,
        activePhotoKey,
        errorName: err?.name,
        errorCode: err?.code,
        errorMessage: err?.message,
        errorStack: err?.stack,
        rawError: err,
      });
      toast.error('Failed to upload image note');
    } finally {
      setIsUploadingImage(false);
      setUploadingPhotoNoteKey(null);
      pendingPhotoUploadKeyRef.current = null;
      replacePhotoUploadRef.current = false;
      if (typeof uploadNoteKey === 'number' && Number.isFinite(uploadNoteKey)) {
        window.setTimeout(() => setLockedNotePage(null), 250);
      }
      debugPhotoNotes('upload cleanup finished', {
        uploadNoteKey,
        activePhotoKey,
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleImageUploadRef = React.useRef(handleImageUpload);
  React.useEffect(() => { handleImageUploadRef.current = handleImageUpload; }, [handleImageUpload]);

  const triggerImageUpload = React.useCallback((options?: { replace?: boolean }) => {
    const targetNoteKey = activeNoteKey;
    replacePhotoUploadRef.current = Boolean(options?.replace);
    pendingPhotoUploadKeyRef.current = targetNoteKey;
    setUploadingPhotoNoteKey(targetNoteKey);
    setNoteMode('photo');
    setIsEditingPhotoNotes(false);
    if (typeof targetNoteKey === 'number' && Number.isFinite(targetNoteKey)) {
      setLockedNotePage(targetNoteKey);
    }
    debugPhotoNotes('upload picker opening', {
      targetNoteKey,
      targetPhotoKey: getPhotoNoteKey(targetNoteKey),
      replace: Boolean(options?.replace),
    });
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.position = 'fixed';
    input.style.top = '0';
    input.style.left = '0';
    input.style.opacity = '0.01';
    input.style.width = '1px';
    input.style.height = '1px';
    input.style.zIndex = '999999';
    input.onchange = (e) => {
      handleImageUploadRef.current(e as any);
      setTimeout(() => input.remove(), 100);
    };
    document.body.appendChild(input);
    input.click();
  }, [activeNoteKey, debugPhotoNotes, getPhotoNoteKey]);

  React.useEffect(() => {
    if (showNoteTakerSplit && editorRef.current) {
      const activeContent = notes[activeNoteKey] || '';
      if (!isPhotoNoteContent(activeContent)) {
        if (editorRef.current.innerHTML !== activeContent) {
          editorRef.current.innerHTML = activeContent;
        }
      }
    }
  }, [showNoteTakerSplit, activeNoteKey, isPhotoNoteContent, notes]);

  React.useEffect(() => {
    setIsEditingPhotoNotes(false);
  }, [activeNoteKey, noteMode]);

  React.useEffect(() => {
    if (noteMode !== 'photo') return;
    debugPhotoNotes('photo viewer render snapshot', {
      activeNoteKey,
      activePhotoKey: getPhotoNoteKey(activeNoteKey),
      resolvedPhotoContent: getPhotoNoteContent(activeNoteKey, notes),
      resolvedPhotoUrls: getPhotoNoteContent(activeNoteKey, notes).replace(/^images?:/, '').split('||').filter(Boolean),
    });
  }, [activeNoteKey, debugPhotoNotes, getPhotoNoteContent, getPhotoNoteKey, noteMode, notes]);

  React.useEffect(() => {
    const prevKey = lastActiveNoteKeyRef.current;
    if (prevKey !== activeNoteKey) {
      if (editorRef.current) {
        const prevText = editorRef.current.innerHTML;
        const existingVal = notesRef.current[prevKey] || '';
        if (!isPhotoNoteContent(existingVal) && existingVal !== prevText) {
          handleSaveNote(prevKey, prevText);
        }
      }
      lastActiveNoteKeyRef.current = activeNoteKey;
    }
  }, [activeNoteKey, isPhotoNoteContent]);

  const [isAutoScrolling, setIsAutoScrolling] = useState(false);
  const [autoScrollSpeed, setAutoScrollSpeed] = useState(20);
  const autoScrollRafRef = useRef<number | null>(null);
  const isAutoScrollingRef = useRef(false);
  const autoScrollSpeedRef = useRef(20);
  const lastScrollTimeRef = useRef(0);
  const [readingTimeMin, setReadingTimeMin] = useState<number | null>(null);

  const saveResumeProgressNow = React.useCallback(async (metrics: { progressPercent: number; currentPage: number; totalPages: number; scrollTop: number }) => {
    if (!resumeProgressEnabled || !subjectId || !auth.currentUser) return;
    try {
      console.log('[DEBUG_RESUME] saveResumeProgressNow saving to Firestore:', metrics);
      const uid = auth.currentUser.uid;
      await setDoc(
        doc(db, `teaching_workspaces/${uid}/courses/${subjectId}/reading_progress`, progressScopeId),
        {
          progressPercent: metrics.progressPercent,
          currentPage: metrics.currentPage,
          totalPages: metrics.totalPages,
          scrollTop: metrics.scrollTop,
          schoolName: selectedSchool,
          className: selectedClass,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Error saving resume progress:', error);
    }
  }, [resumeProgressEnabled, progressScopeId, selectedClass, selectedSchool, subjectId]);

  const persistResumeProgress = React.useCallback((metrics: { progressPercent: number; currentPage: number; totalPages: number; scrollTop: number }) => {
    if (!resumeProgressEnabled || !subjectId || !auth.currentUser) return;

    const nextProgress = {
      progressPercent: metrics.progressPercent,
      currentPage: metrics.currentPage,
      totalPages: metrics.totalPages,
      scrollTop: metrics.scrollTop,
    };

    savedProgressRef.current = nextProgress;
    latestProgressMetricsRef.current = nextProgress;
    hasUnsavedProgressRef.current = true;

    if (saveProgressTimeoutRef.current) {
      clearTimeout(saveProgressTimeoutRef.current);
    }

    saveProgressTimeoutRef.current = setTimeout(async () => {
      await saveResumeProgressNow(nextProgress);
      hasUnsavedProgressRef.current = false;
    }, 180);
  }, [resumeProgressEnabled, saveResumeProgressNow, subjectId]);

  React.useEffect(() => {
    if (!resumeProgressEnabled) return;
    const flushProgress = () => {
      if (!hasUnsavedProgressRef.current) return;
      void saveResumeProgressNow(latestProgressMetricsRef.current);
      hasUnsavedProgressRef.current = false;
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushProgress();
    };
    window.addEventListener('pagehide', flushProgress);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', flushProgress);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      flushProgress();
    };
  }, [resumeProgressEnabled, saveResumeProgressNow]);

  const hasReadableContent = Boolean(pdfUrl || htmlContent || isFullSubjectView);

  // Content-area loader: shown briefly while items / content are being
  // hydrated from the route state. Mirrors the loader pattern in
  // CourseOutlineEditor — covers the content area so the user never sees
  // a flash of the previous subject's content or an empty state.
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  React.useEffect(() => {
    if (hasReadableContent) {
      // give the React tree a tick to commit before hiding the loader
      const t = window.setTimeout(() => setIsLoadingContent(false), 0);
      return () => window.clearTimeout(t);
    }
  }, [hasReadableContent]);
  const eyeCareStyle = eyeCareStrength > 0
    ? {
        filter: `sepia(${eyeCareStrength * 0.45}%) saturate(${100 - eyeCareStrength * 0.25}%) brightness(${100 - eyeCareStrength * 0.16}%) contrast(${100 - eyeCareStrength * 0.08}%)`,
        backgroundColor: `rgba(245, 222, 179, ${eyeCareStrength / 320})`,
      }
    : undefined;

  React.useEffect(() => {
    if (!isFullscreenMode) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFullscreenMode(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreenMode]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.ms-powerpoint'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Please upload a valid PDF or PowerPoint file");
        return;
      }
      const url = URL.createObjectURL(file);
      setPdfUrl(url);
      setHtmlContent(null);
      toast.success("Module reference loaded");
    }
  };

  const clearPdf = () => {
    if (pdfUrl || htmlContent) {
      if (pdfUrl && !directPdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
      setHtmlContent(null);
      setNumPages(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (onCloseDirect) onCloseDirect();
    }
  };

  React.useEffect(() => {
    const url = directPdfUrl || routePdfUrl;
    const content = (location.state as any)?.htmlContent;
    if (url) {
      setPdfUrl(url);
      setHtmlContent(null);
    } else if (content) {
      setHtmlContent(content);
      setPdfUrl(null);
    }
  }, [directPdfUrl, routePdfUrl, location.state]);

  // When the user picks a different item from the items tab bar, swap what's
  // being rendered. PDFs become the active PDF; outlines populate the htmlContent.
  const handleItemSwitch = (item: any) => {
    setActiveItemId(item.id);
    setItems(prev => prev.map(x => (x.id === item.id ? { ...x, active: true } : { ...x, active: false })));
    if (item.type === 'pdf' && item.url) {
      setPdfUrl(item.url);
      setHtmlContent(null);
    } else if (item.type === 'outline') {
      setHtmlContent(item.content || '');
      setPdfUrl(null);
    }
  };

  // Auto-activate the first item if we have items but no content rendered yet.
  // Without this, the empty state would briefly flash on subtopics with only
  // multi-content and no legacy `pdfUrl` / `htmlContent` props.
  React.useEffect(() => {
    if (items.length > 0 && !pdfUrl && !htmlContent && !activeItemId) {
      handleItemSwitch(items[0]);
    }
  }, [items, pdfUrl, htmlContent, activeItemId]);

  React.useEffect(() => {
    hasRestoredProgressRef.current = !resumeProgressEnabled;
    savedProgressRef.current = { progressPercent: 0, currentPage: 0, totalPages: 0, scrollTop: 0 };
    latestProgressMetricsRef.current = { progressPercent: 0, currentPage: 0, totalPages: 0, scrollTop: 0 };
    hasUnsavedProgressRef.current = false;
    setRestoreTarget({ progressPercent: 0, currentPage: 0, scrollTop: 0, ready: false });
    setScrollProgress(0);
    setIsRestoringProgress(resumeProgressEnabled);
  }, [resumeProgressEnabled, progressScopeId, subjectId]);

  React.useEffect(() => {
    if (!resumeProgressEnabled || !subjectId || !auth.currentUser) return;

    let isCancelled = false;
    let retryCount = 0;
    const MAX_RETRIES = 15;

    const loadSavedProgress = async () => {
      try {
        const uid = auth.currentUser?.uid;
        if (!uid) return;

        // When forceProgressReload is true (Start Again flow), bypass Firestore cache
        // so we never read the pre-reset cached value.
        const progressRef = doc(db, `teaching_workspaces/${uid}/courses/${subjectId}/reading_progress`, progressScopeId);
        const progressSnap = forceProgressReload
          ? await getDoc(progressRef).then(() => getDoc(progressRef))  // double-fetch to bust cache
          : await getDoc(progressRef);
        if (isCancelled) return;

        if (progressSnap.exists()) {
          const data = progressSnap.data();
          const progressPercent = Number(data.progressPercent || 0);
          const isCompleted = data.isCompleted === true;
          
          if (forceProgressReload) {
            if (progressPercent === 0 && !isCompleted) {
              hasRestoredProgressRef.current = false;
              savedProgressRef.current = { progressPercent: 0, currentPage: 0, totalPages: Number(data.totalPages || 0), scrollTop: 0 };
              setWaitingForReset(false);
              setRestoreTarget({ progressPercent: 0, currentPage: 0, scrollTop: 0, ready: true });
              setScrollProgress(0);
              setIsCompleted(false);
              setCurrentPage(0);
              setTotalPageCount(0);
              if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
            } else if (retryCount < MAX_RETRIES) {
              retryCount++;
              if (!isCancelled) setTimeout(loadSavedProgress, 300);
            } else {
              setWaitingForReset(false);
              savedProgressRef.current = { progressPercent: 0, currentPage: 0, totalPages: 0, scrollTop: 0 };
              setRestoreTarget({ progressPercent: 0, currentPage: 0, scrollTop: 0, ready: true });
              setScrollProgress(0);
              setIsCompleted(false);
            }
          } else {
            const loaded = {
              progressPercent: progressPercent,
              currentPage: Number(data.currentPage || 0),
              totalPages: Number(data.totalPages || 0),
              scrollTop: Number(data.scrollTop || 0),
            };
            console.log('[DEBUG_RESUME] loadSavedProgress read from Firestore:', loaded);
            savedProgressRef.current = loaded;
            setRestoreTarget({ progressPercent: loaded.progressPercent, currentPage: loaded.currentPage, scrollTop: loaded.scrollTop, ready: true });
            setScrollProgress(loaded.progressPercent);
            if (isCompleted) {
              setIsCompleted(true);
              if (data.completedAt?.toDate) {
                setCompletedAt(data.completedAt.toDate().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
              }
            }
          }
        } else {
          setRestoreTarget({ progressPercent: 0, currentPage: 0, scrollTop: 0, ready: true });
          setScrollProgress(0);
          setIsCompleted(false);
          if (forceProgressReload) setWaitingForReset(false);
        }
      } catch (error) {
        console.error('Error loading resume progress:', error);
        if (forceProgressReload) setWaitingForReset(false);
        setRestoreTarget({ progressPercent: 0, currentPage: 0, scrollTop: 0, ready: true });
      }
    };

    loadSavedProgress();

    return () => {
      isCancelled = true;
      if (saveProgressTimeoutRef.current) {
        clearTimeout(saveProgressTimeoutRef.current);
      }
      if (hasUnsavedProgressRef.current) {
        void saveResumeProgressNow(latestProgressMetricsRef.current);
        hasUnsavedProgressRef.current = false;
      }
    };
  }, [resumeProgressEnabled, progressScopeId, subjectId, forceProgressReload, saveResumeProgressNow]);

  React.useEffect(() => {
    if (!resumeProgressEnabled || !scrollContainerRef.current) return;
    if (!restoreTarget.ready) return;
    if (hasRestoredProgressRef.current) return;

    const container = scrollContainerRef.current;

    // Nothing to restore — just reset counters and mark done
    if (restoreTarget.scrollTop === 0 && restoreTarget.progressPercent === 0) {
      console.log('[DEBUG_RESUME] restore target is 0, no-op restore');
      hasRestoredProgressRef.current = true;
      container.scrollTop = 0;
      const pageEls = Array.from(container.querySelectorAll<HTMLElement>('[data-progress-page="true"]'));
      if (pageEls.length > 0) setTotalPageCount(pageEls.length);
      setIsRestoringProgress(false); // hide overlay immediately for fresh reads
      return;
    }

    const syncPageCounters = () => {
      const pageEls = Array.from(container.querySelectorAll<HTMLElement>('[data-progress-page="true"]'));
      if (pageEls.length > 0) {
        setTotalPageCount(pageEls.length);
        const containerRect = container.getBoundingClientRect();
        let closestPage = 1;
        let minDist = Infinity;
        pageEls.forEach((el, idx) => {
          const dist = Math.abs(el.getBoundingClientRect().top - containerRect.top);
          if (dist < minDist) { minDist = dist; closestPage = idx + 1; }
        });
        setCurrentPage(closestPage);
      }
    };

    // Compute the target scroll position.
    // PRIORITY: use the saved absolute scrollTop.
    // GUARD: if the saved scrollTop implies a wildly different percentage than the
    //        stored progressPercent, it means scrollTop was captured against a partially-
    //        loaded layout (smaller scrollHeight). In that case, trust progressPercent.
    const computeTarget = (): number => {
      const maxScroll = Math.max(container.scrollHeight - container.clientHeight, 0);
      if (maxScroll <= 0) return 0;

      const percentTarget = Math.round((restoreTarget.progressPercent / 100) * maxScroll);

      if (restoreTarget.scrollTop > 0 && restoreTarget.scrollTop <= maxScroll) {
        const impliedPercent = (restoreTarget.scrollTop / maxScroll) * 100;
        const delta = Math.abs(impliedPercent - restoreTarget.progressPercent);
        if (delta > 10) {
          // scrollTop is stale (was saved against a smaller partial layout).
          // Trust progressPercent for the real position.
          console.log(`[DEBUG_RESUME] computeTarget: scrollTop=${restoreTarget.scrollTop} implies ${impliedPercent.toFixed(1)}% but stored is ${restoreTarget.progressPercent.toFixed(1)}% — delta=${delta.toFixed(1)}% > 10. Using percentTarget=${percentTarget}`);
          return percentTarget;
        }
        return restoreTarget.scrollTop;
      }
      return percentTarget;
    };

    const doFinalRestore = () => {
      const target = computeTarget();
      console.log(`[DEBUG_RESUME] doFinalRestore executing to scrollTop=${target}. Layout was stable.`);
      container.scrollTop = target;
      setScrollProgress(restoreTarget.progressPercent);
      requestAnimationFrame(syncPageCounters);
      // Hide the overlay with a short delay so the layout fully paints before revealing
      setTimeout(() => setIsRestoringProgress(false), 300);
    };

    // --- Stable-layout detector ---
    // PDFs load lazily — scrollHeight grows as each page renders.
    // We poll every 80ms and track how many consecutive polls have the same height.
    // Once height is stable for 5 consecutive polls (= 400ms), we do the final restore.
    // We also apply intermediate restores while waiting so the user doesn't see top-of-page.
    let lastHeight = -1;
    let stableCount = 0;
    const STABLE_HITS_NEEDED = 5;   // 5 × 80ms = 400ms of layout stability
    const CHECK_INTERVAL_MS = 80;

    const checkAndRestore = () => {
      const h = container.scrollHeight;

      if (h !== lastHeight) {
        // Layout still growing — apply intermediate restore so position tracks progress
        const maxScroll = Math.max(h - container.clientHeight, 0);
        if (maxScroll > 0) {
          const target = restoreTarget.scrollTop > 0 && restoreTarget.scrollTop <= maxScroll
            ? restoreTarget.scrollTop
            : Math.round((restoreTarget.progressPercent / 100) * maxScroll);
          container.scrollTop = target;
          console.log(`[DEBUG_RESUME] checkAndRestore intermediate jump: scrollHeight changed from ${lastHeight} to ${h}. Set scrollTop=${target}`);
        }
        lastHeight = h;
        stableCount = 0;
      } else {
        // Only increment stable count if all PDFs have actually finished loading!
        // Otherwise, a network pause of 400ms will trick us into aborting the restore.
        if (isFullyLoadedRef.current) {
          stableCount++;
          console.log(`[DEBUG_RESUME] checkAndRestore stable count: ${stableCount}/${STABLE_HITS_NEEDED} for scrollHeight=${h}`);
        } else {
          console.log(`[DEBUG_RESUME] checkAndRestore layout paused at ${h}, but PDFs still loading... waiting.`);
        }
      }

      if (stableCount >= STABLE_HITS_NEEDED) {
        // Layout is stable AND fully loaded — do the definitive final restore
        doFinalRestore();
        hasRestoredProgressRef.current = true;
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      }
    };

    // Run immediately so we start restoring right away
    console.log('[DEBUG_RESUME] Beginning stable-layout restore effect. Target:', restoreTarget);
    // Only show the overlay when there is actual saved progress to restore
    if (restoreTarget.progressPercent > 0 || restoreTarget.scrollTop > 0) {
      setIsRestoringProgress(true);
    }
    checkAndRestore();
    const intervalId = setInterval(checkAndRestore, CHECK_INTERVAL_MS);

    // Absolute hard timeout — ensures restore always happens even for very large PDFs
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      if (!hasRestoredProgressRef.current) {
        console.log('[DEBUG_RESUME] Hard timeout hit (12s). Executing doFinalRestore anyway.');
        doFinalRestore();
        hasRestoredProgressRef.current = true;
      }
    }, 12000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [resumeProgressEnabled, restoreTarget, fullSyllabus, fullSubContentMap]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setTotalPageCount(numPages);
  }

  const htmlTextOnly = React.useMemo(() => {
    if (!htmlContent) return '';
    return (htmlContent || '')
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;|&#160;/gi, ' ')
      .replace(/&[a-z0-9#]+;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }, [htmlContent]);

  const htmlHasNonTextContent = React.useMemo(
    () => /<(img|iframe|video|audio|table|ul|ol|li|blockquote|pre|code|embed|object|svg|hr)\b/i.test(htmlContent || ''),
    [htmlContent]
  );

  const handleDebugClick = () => {
    const container = scrollContainerRef.current;

    // Always populate basic debug info, even if scroll container isn't ready yet.
    const baseData: Record<string, unknown> = {
      timestamp: new Date().toISOString(),
      raw: {
        locationStateKeys: location.state ? Object.keys(location.state) : null,
        locationStateHtmlContentType: location.state ? typeof (location.state as any).htmlContent : 'no-state',
        locationStateHtmlContentLength: location.state && (location.state as any).htmlContent != null
          ? String((location.state as any).htmlContent).length
          : 0,
        locationStateHtmlContentPreview: location.state && (location.state as any).htmlContent != null
          ? String((location.state as any).htmlContent).substring(0, 500)
          : null,
        locationStateDirectPdfUrl: location.state ? (location.state as any).directPdfUrl ?? null : null,
        localStorageHtmlContentLength: (() => {
          try {
            const v = localStorage.getItem('vochak_viewer_htmlContent');
            return v ? JSON.parse(v).length : 0;
          } catch { return -1; }
        })(),
      },
      view: {
        isFullSubjectView,
        hasReadableContent,
        pdfUrlPresent: Boolean(pdfUrl),
        htmlContentPresent: Boolean(htmlContent),
        htmlContentLength: htmlContent ? htmlContent.length : 0,
        htmlContentPreview: htmlContent ? htmlContent.substring(0, 500) : null,
        htmlContentTextLength: htmlTextOnly.length,
        htmlContentTextPreview: htmlTextOnly.substring(0, 300),
        htmlContentHasNonTextContent: htmlHasNonTextContent,
        directPdfUrlPresent: Boolean(directPdfUrl),
        resolvedPdfUrlPresent: Boolean(resolvedPdfUrl),
        subjectId,
        subjectName,
        contentTitle,
        numPages,
        currentPage,
        currentPdfPage,
        totalPageCount,
        zoom,
        eyeCareStrength,
        scrollProgress,
        isCompleted,
        completedAt,
        resumeProgressEnabled,
        loadedPdfsCount,
        totalExpectedPdfs,
        hasRestored: hasRestoredProgressRef.current,
        restoreTarget,
        savedProgress: savedProgressRef.current,
        latestProgressMetrics: latestProgressMetricsRef.current,
      }
    };

    if (!container) {
      baseData.warning = 'scrollContainerRef.current is null — scroll metrics unavailable';
      setDebugData(JSON.stringify(baseData, null, 2));
      setShowDebugModal(true);
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = container;
    const pages = getTrackablePages(container);
    const totalPages = pages.length;

    const method1_standard = Math.max(0, Math.min(100, (scrollTop / Math.max(scrollHeight - clientHeight, 1)) * 100));

    let method2_visual = 0;
    if (totalPages > 0) {
      const containerRect = container.getBoundingClientRect();
      let closestIndex = 0;
      let minDist = Infinity;
      pages.forEach((page, idx) => {
        const dist = Math.abs(page.getBoundingClientRect().top - containerRect.top);
        if (dist < minDist) { minDist = dist; closestIndex = idx; }
      });
      const activePage = pages[closestIndex];
      const pageRect = activePage.getBoundingClientRect();
      const pageFraction = Math.max(0, Math.min(1, (containerRect.top - pageRect.top) / pageRect.height));
      method2_visual = Math.min(100, Math.max(0, ((closestIndex + pageFraction) / totalPages) * 100));
    }

    let method3_bounds = 0;
    if (container.firstElementChild && container.lastElementChild) {
      const firstTop = container.firstElementChild.getBoundingClientRect().top;
      const lastBottom = container.lastElementChild.getBoundingClientRect().bottom;
      const totalContentHeight = lastBottom - firstTop;
      const scrollOffset = container.getBoundingClientRect().top - firstTop;
      method3_bounds = Math.max(0, Math.min(100, (scrollOffset / Math.max(totalContentHeight - clientHeight, 1)) * 100));
    }

    let method4_cumulative = 0;
    let totalOffsetHeight = 0;
    let runningHeight = 0;
    Array.from(container.children).forEach((child: Element) => {
      totalOffsetHeight += (child as HTMLElement).offsetHeight || 0;
    });
    if (totalOffsetHeight > 0) {
      for (const child of Array.from(container.children)) {
        const h = (child as HTMLElement).offsetHeight || 0;
        if (runningHeight + h >= scrollTop) {
          const fraction = (scrollTop - runningHeight) / Math.max(h, 1);
          runningHeight += h * fraction;
          break;
        }
        runningHeight += h;
      }
      method4_cumulative = Math.max(0, Math.min(100, (runningHeight / Math.max(totalOffsetHeight - clientHeight, 1)) * 100));
    }

    let method5_offsetTop = 0;
    if (totalPages > 0) {
      let closestIndex = 0;
      let minDist = Infinity;
      pages.forEach((page, idx) => {
        const dist = Math.abs(page.offsetTop - scrollTop);
        if (dist < minDist) { minDist = dist; closestIndex = idx; }
      });
      method5_offsetTop = (closestIndex / Math.max(totalPages - 1, 1)) * 100;
    }

    const data = {
      ...baseData,
      container: { scrollTop, scrollHeight, clientHeight, totalPages },
      restoreState: { hasRestoredProgressRef: hasRestoredProgressRef.current, restoreTarget },
      methods: {
        standard: method1_standard,
        visual: method2_visual,
        bounds: method3_bounds,
        cumulative: method4_cumulative,
        offsetTop: method5_offsetTop
      },
      savedProgressRef: savedProgressRef.current
    };

    setDebugData(JSON.stringify(data, null, 2));
    setShowDebugModal(true);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // DO NOT track scroll progress or save to Firestore until the initial restore completes.
    // Otherwise, programmatic scrolls during the restore phase against a partially
    // loaded document will calculate incorrect percentages (like 98%) and overwrite
    // the real saved progress in Firestore.
    if (!hasRestoredProgressRef.current) return;

    const metrics = getScrollMetrics(e.currentTarget);
    
    // Only update the progress percentage if all PDFs have fully loaded their layouts!
    // Otherwise, the scrollHeight is incomplete, and the calculation will jump erratically (e.g., from 13% to 98%).
    if (isFullyLoaded) {
      setScrollProgress(metrics.progressPercent || 0);
    }

    if (isFullSubjectView) {
      if (resumeProgressEnabled && Math.random() < 0.05) { // log only 5% of scroll events to prevent spam
         console.log('[DEBUG_RESUME] handleScroll triggered, persisting metrics:', metrics);
      }
      
      if (resumeProgressEnabled) {
        // We still want to persist the exact pixel position (scrollTop) even if it's not fully loaded.
        // But we prevent the broken progressPercent from overwriting the good one.
        persistResumeProgress({
          ...metrics,
          progressPercent: isFullyLoaded ? metrics.progressPercent : latestProgressMetricsRef.current.progressPercent,
          currentPage: isFullyLoaded ? metrics.currentPage : latestProgressMetricsRef.current.currentPage,
        });
      }

      const container = e.currentTarget;
      const pageEls = Array.from(container.querySelectorAll<HTMLElement>('[data-progress-page="true"]'));
      if (pageEls.length > 0) {
        setTotalPageCount(pageEls.length);
        if (isResizingNotePaneRef.current) return;
        const containerRect = container.getBoundingClientRect();
        let closestPage = 1;
        let minDist = Infinity;
        pageEls.forEach((el, idx) => {
          const dist = Math.abs(el.getBoundingClientRect().top - containerRect.top);
          if (dist < minDist) { minDist = dist; closestPage = idx + 1; }
        });
        setCurrentPage(closestPage);
      }
    } else if (pdfUrl) {
      const container = e.currentTarget;
      const pageEls = Array.from(container.querySelectorAll<HTMLElement>('.document-page-container'));
      if (pageEls.length > 0) {
        setTotalPageCount(pageEls.length);
        const containerRect = container.getBoundingClientRect();
        let closestPage = 1;
        let minDist = Infinity;
        pageEls.forEach((el) => {
          const dist = Math.abs(el.getBoundingClientRect().top - containerRect.top);
          if (dist < minDist) {
            minDist = dist;
            const pageNumAttr = el.getAttribute('data-page-num');
            if (pageNumAttr) closestPage = parseInt(pageNumAttr, 10);
          }
        });
        setCurrentPdfPage(closestPage);
      }
    }
  };

  const handleBack = () => {
    // 1. Prioritize natural browser history back for a true "go back to where I was" experience.
    const canGoBack =
      typeof window !== 'undefined' &&
      typeof window.history.state?.idx === 'number' &&
      window.history.state.idx > 0;
      
    if (canGoBack) {
      navigate(-1);
      return;
    }

    // 2. Fallback to manually constructed path if opened directly without history
    const s = location.state as any;
    const finalFromPath = s?.fromPath || fromPath;
    const finalFromState = s?.fromState || fromState;
    const finalSelectedSchool = s?.selectedSchool || selectedSchool;
    const finalSelectedClass = s?.selectedClass || selectedClass;
    const finalSubjectId = s?.subjectId || subjectId;
    const finalSubjectName = s?.subjectName || subjectName;
    const finalIsFirebaseSubject = s?.isFirebaseSubject || (location.state as any)?.isFirebaseSubject;

    if (finalFromPath) {
      navigate(finalFromPath, {
        replace: true,
        state: finalFromState
          ? { ...finalFromState, selectedSchool: finalSelectedSchool, selectedClass: finalSelectedClass }
          : {
              subjectId: finalSubjectId,
              subjectName: finalSubjectName,
              isFirebaseSubject: finalIsFirebaseSubject,
              selectedSchool: finalSelectedSchool,
              selectedClass: finalSelectedClass,
            },
      });
      return;
    }

    // 3. Absolute fallback
    navigate('/dashboard/lessons', { replace: true });
  };

  const handleMarkComplete = async () => {
    if (!resumeProgressEnabled || !subjectId || !auth.currentUser || isMarkingComplete) return;
    setIsMarkingComplete(true);
    try {
      const uid = auth.currentUser.uid;
      await setDoc(
        doc(db, `teaching_workspaces/${uid}/courses/${subjectId}/reading_progress`, progressScopeId),
        {
          isCompleted: true,
          completedAt: serverTimestamp(),
          progressPercent: 100,
          schoolName: selectedSchool,
          className: selectedClass,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setIsCompleted(true);
      setScrollProgress(100);
      const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      setCompletedAt(today);
    } catch (err) {
      console.error('Error marking complete:', err);
    } finally {
      setIsMarkingComplete(false);
    }
  };

  const scrollToTopicId = (topicId: string) => {
    const el = scrollContainerRef.current?.querySelector<HTMLElement>(`[data-topic-id="${topicId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setShowToolsPanel(false);
  };

  const scrollToSubTopicId = (subTopicId: string) => {
    const el = scrollContainerRef.current?.querySelector<HTMLElement>(`[data-subtopic-id="${subTopicId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setShowToolsPanel(false);
  };

  const handleJumpToPage = (e: React.FormEvent) => {
    e.preventDefault();
    const page = parseInt(jumpToInput, 10);
    if (isNaN(page) || !scrollContainerRef.current) return;
    const pages = getTrackablePages(scrollContainerRef.current);
    const target = pages[Math.max(0, page - 1)];
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setJumpToInput('');
  };

  React.useEffect(() => {
    if (!isFullSubjectView || !fullSyllabus || !fullSubContentMap) return;
    let totalWords = 0;
    fullSyllabus.topics?.forEach((topic: any) => {
      const td = fullSubContentMap[`${topic.id}_main`];
      if (!topic.subTopics?.length && td?.outlineContent) {
        totalWords += td.outlineContent.replace(/<[^>]*>/g, ' ').split(/\s+/).filter(Boolean).length;
      }
      topic.subTopics?.forEach((sub: any, subIndex: number) => {
        const sd = getCanonicalSubTopicContent(topic, sub, subIndex);
        if (sd?.outlineContent) totalWords += sd.outlineContent.replace(/<[^>]*>/g, ' ').split(/\s+/).filter(Boolean).length;
      });
    });
    if (totalWords > 0) setReadingTimeMin(Math.max(1, Math.round(totalWords / 200)));
  }, [isFullSubjectView, fullSyllabus, fullSubContentMap, getCanonicalSubTopicContent]);

  React.useEffect(() => {
    if (!isFullSubjectView || !subjectId || !auth.currentUser) return;
    const loadBookmarks = async () => {
      try {
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        const snap = await getDocs(collection(db, `teaching_workspaces/${uid}/courses/${subjectId}/bookmarks`));
        const bms: ReaderBookmark[] = [];
        snap.forEach(d => bms.push({ id: d.id, ...d.data() } as ReaderBookmark));
        bms.sort((a, b) => a.scrollTop - b.scrollTop);
        setBookmarks(bms);
      } catch (e) { console.error('Error loading bookmarks:', e); }
    };
    loadBookmarks();
  }, [isFullSubjectView, subjectId]);

  const handleAddBookmark = async () => {
    if (!subjectId || !auth.currentUser || isSavingBookmark || !scrollContainerRef.current) return;
    setIsSavingBookmark(true);
    try {
      const uid = auth.currentUser.uid;
      const container = scrollContainerRef.current;
      const metrics = getScrollMetrics(container);
      let topicTitle = 'Start';
      let subTopicTitle: string | undefined;
      const topicEls = Array.from(container.querySelectorAll<HTMLElement>('[data-topic-id]'));
      for (const el of topicEls) {
        if (el.getBoundingClientRect().top <= 120) {
          topicTitle = el.querySelector('h2')?.textContent || topicTitle;
        }
      }
      const subEls = Array.from(container.querySelectorAll<HTMLElement>('[data-subtopic-id]'));
      for (const el of subEls) {
        if (el.getBoundingClientRect().top <= 120) {
          subTopicTitle = el.querySelector('span:last-child')?.textContent || undefined;
        }
      }
      const label = `${Math.round(metrics.progressPercent)}% — ${topicTitle}${subTopicTitle ? ` › ${subTopicTitle}` : ''}`;
      const docRef = await addDoc(
        collection(db, `teaching_workspaces/${uid}/courses/${subjectId}/bookmarks`),
        { label, scrollTop: metrics.scrollTop, topicTitle, subTopicTitle: subTopicTitle || '', progressPercent: metrics.progressPercent, createdAt: new Date().toISOString() }
      );
      setBookmarks(prev => [...prev, { id: docRef.id, label, scrollTop: metrics.scrollTop, topicTitle, subTopicTitle, createdAt: new Date().toISOString() }].sort((a, b) => a.scrollTop - b.scrollTop));
      toast.success('Bookmark saved');
    } catch (e) { console.error('Error adding bookmark:', e); toast.error('Failed to save bookmark'); }
    finally { setIsSavingBookmark(false); }
  };

  const handleDeleteBookmark = async (bmId: string) => {
    if (!subjectId || !auth.currentUser) return;
    try {
      const uid = auth.currentUser.uid;
      await deleteDoc(doc(db, `teaching_workspaces/${uid}/courses/${subjectId}/bookmarks`, bmId));
      setBookmarks(prev => prev.filter(b => b.id !== bmId));
    } catch (e) { console.error('Error deleting bookmark:', e); }
  };

  const jumpToBookmark = (bm: ReaderBookmark) => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollTo({ top: bm.scrollTop, behavior: 'smooth' });
  };

  React.useEffect(() => {
    if (!subjectId || !auth.currentUser) return;
    const loadNotes = async () => {
      try {
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        const notesDoc = await getDoc(doc(db, `teaching_workspaces/${uid}/courses/${subjectId}/notes`, 'pages_notes'));
        if (notesDoc.exists()) {
          setNotes(notesDoc.data()?.notes || {});
        }
      } catch (e) {
        console.error('Error loading notes:', e);
      }
    };
    loadNotes();
  }, [subjectId]);

  React.useEffect(() => { notesRef.current = notes; }, [notes]);

  const handleSaveNote = async (key: string | number, text: string) => {
    if (!subjectId || !auth.currentUser) return;
    setIsSavingNotes(true);
    try {
      const uid = auth.currentUser.uid;
      const latestNotes = notesRef.current;
      const updatedNotes = { ...latestNotes, [key]: text };
      if (!text.trim()) {
        delete updatedNotes[key];
      }
      await setDoc(doc(db, `teaching_workspaces/${uid}/courses/${subjectId}/notes`, 'pages_notes'), {
        notes: updatedNotes,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      setNotes(prev => {
        const merged = { ...prev, [key]: text };
        if (!text.trim()) delete merged[key];
        return merged;
      });
      toast.success('Notes saved');
    } catch (e) {
      console.error('Error saving notes:', e);
      toast.error('Failed to save notes');
    } finally {
      setIsSavingNotes(false);
    }
  };

  const flushActiveTextNote = () => {
    if (noteMode !== 'text' || !editorRef.current || isPhotoNoteContent(notesRef.current[activeNoteKey])) return;
    const htmlVal = editorRef.current.innerHTML;
    setNotes(prev => ({ ...prev, [activeNoteKey]: htmlVal }));
    void handleSaveNote(activeNoteKey, htmlVal);
  };

  const toggleNotePaneWidth = () => {
    const container = scrollContainerRef.current;
    const frozenKey = activeNoteKey;

    if (editorRef.current && !isPhotoNoteContent(notesRef.current[frozenKey])) {
      const htmlVal = editorRef.current.innerHTML;
      setNotes(prev => ({ ...prev, [frozenKey]: htmlVal }));
    }

    pendingNotePaneRestoreRef.current = {
      scrollTop: container?.scrollTop ?? 0,
      page: frozenKey as number,
    };
    isResizingNotePaneRef.current = true;
    setLockedNotePage(Number(activeNoteKey));
    setNotePaneWidth(w => w === 'wide' ? 'normal' : 'wide');

    window.setTimeout(() => {
      const restore = pendingNotePaneRestoreRef.current;
      if (restore && scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = restore.scrollTop;
        setCurrentPage(restore.page);
      }
    }, 80);

    window.setTimeout(() => {
      isResizingNotePaneRef.current = false;
      pendingNotePaneRestoreRef.current = null;
      setLockedNotePage(null);
    }, 360);
  };

  const getNotePreview = (value: string) => {
    if (!value) return '';
    if (value.startsWith('image:')) return 'Photo note';
    if (value.startsWith('images:')) return 'Photo gallery';
    const div = document.createElement('div');
    div.innerHTML = value;
    return (div.textContent || '').replace(/\s+/g, ' ').trim();
  };

  // Group notes into entries by base key (ignoring _photos suffix for grouping)
  const groupedNotes = Object.entries(notes).reduce((acc, [key, value]) => {
    if (!value?.trim()) return acc;
    const isPhotoKey = String(key).endsWith('_photos');
    const baseKey = isPhotoKey ? String(key).replace('_photos', '') : String(key);
    
    if (!acc[baseKey]) {
      acc[baseKey] = { textPreview: '', hasPhotos: false, photoCount: 0 };
    }
    
    if (isPhotoKey || isPhotoNoteContent(value)) {
      acc[baseKey].hasPhotos = true;
      acc[baseKey].photoCount = value.replace(/^images?:/, '').split('||').filter(Boolean).length;
    } else {
      acc[baseKey].textPreview = getNotePreview(value);
    }
    return acc;
  }, {} as Record<string, { textPreview: string; hasPhotos: boolean; photoCount: number }>);

  const notesIndexEntries = Object.keys(groupedNotes).map(baseKey => {
    const data = groupedNotes[baseKey];
    const isSubject = baseKey === 'subject_global';
    return {
      key: baseKey,
      isSubject,
      page: isSubject ? -1 : Number(baseKey),
      preview: data.textPreview,
      hasPhotos: data.hasPhotos,
      photoCount: data.photoCount
    };
  })
  .filter(entry => entry.isSubject || Number.isFinite(entry.page))
  .sort((a, b) => a.isSubject ? -1 : (b.isSubject ? 1 : a.page - b.page));

  const jumpToNotePage = (key: string | number) => {
    if (editorRef.current && !isPhotoNoteContent(notesRef.current[activeNoteKey])) {
      setNotes(prev => ({ ...prev, [activeNoteKey]: editorRef.current?.innerHTML || '' }));
    }
    
    if (key === 'subject_global') {
      setNoteScope('subject');
      setShowNotesIndex(false);
      return;
    }
    
    const page = Number(key);
    setNoteScope('page');
    setLockedNotePage(page);
    setCurrentPage(page);
    setCurrentPdfPage(page);

    const container = scrollContainerRef.current;
    const pages = getTrackablePages(container);
    if (container && pages[page - 1]) {
      pages[page - 1].scrollIntoView({ block: 'start', behavior: 'smooth' });
    }

    window.setTimeout(() => setLockedNotePage(null), 500);
  };

  const startQuickSearchCapture = () => {
    stopAutoScroll();
    setShowToolsPanel(false);
    setIsQuickSearchSelecting(true);
    setQuickSearchStart(null);
    setQuickSearchRect(null);
  };

  const cancelQuickSearchCapture = () => {
    setIsQuickSearchSelecting(false);
    setQuickSearchStart(null);
    setQuickSearchRect(null);
    setIsQuickSearchCapturing(false);
  };

  const getQuickSearchPoint = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>, useChanged = false) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    let clientX, clientY;
    if ('touches' in event) {
      // on touchend, touches is empty — use changedTouches instead
      const touchList = useChanged ? event.changedTouches : event.touches;
      if (!touchList || touchList.length === 0) return null;
      clientX = touchList[0].clientX;
      clientY = touchList[0].clientY;
    } else {
      clientX = (event as React.MouseEvent<HTMLDivElement>).clientX;
      clientY = (event as React.MouseEvent<HTMLDivElement>).clientY;
    }
    return {
      x: Math.max(0, Math.min(bounds.width, clientX - bounds.left)),
      y: Math.max(0, Math.min(bounds.height, clientY - bounds.top)),
    };
  };

  const handleQuickSearchMouseDown = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (isQuickSearchCapturing) return;
    if (!('touches' in event)) {
      event.preventDefault(); // Prevent text selection/drag hijacking
    }
    const point = getQuickSearchPoint(event);
    if (!point) return;
    setQuickSearchStart(point);
    setQuickSearchRect({ x: point.x, y: point.y, width: 0, height: 0 });
  };

  const handleQuickSearchMouseMove = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!quickSearchStart || isQuickSearchCapturing) return;
    const point = getQuickSearchPoint(event);
    if (!point) return;
    setQuickSearchRect({
      x: Math.min(quickSearchStart.x, point.x),
      y: Math.min(quickSearchStart.y, point.y),
      width: Math.abs(point.x - quickSearchStart.x),
      height: Math.abs(point.y - quickSearchStart.y),
    });
  };

  const handleQuickSearchTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    // changedTouches has the finger that was lifted — use it to finalise the rect
    const bounds = event.currentTarget.getBoundingClientRect();
    const touch = event.changedTouches[0];
    if (touch && quickSearchStart) {
      const x2 = Math.max(0, Math.min(bounds.width, touch.clientX - bounds.left));
      const y2 = Math.max(0, Math.min(bounds.height, touch.clientY - bounds.top));
      setQuickSearchRect({
        x: Math.min(quickSearchStart.x, x2),
        y: Math.min(quickSearchStart.y, y2),
        width: Math.abs(x2 - quickSearchStart.x),
        height: Math.abs(y2 - quickSearchStart.y),
      });
    }
    // small delay so state settles before finishQuickSearchCapture reads it
    setTimeout(() => {
      finishQuickSearchCapture();
    }, 30);
  };

  const finishQuickSearchCapture = async (event?: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    const overlay = event?.currentTarget || quickSearchOverlayRef.current;
    if (!quickSearchRect || !scrollContainerRef.current || !overlay || quickSearchRect.width < 24 || quickSearchRect.height < 24) {
      cancelQuickSearchCapture();
      return;
    }

    setIsQuickSearchCapturing(true);
    try {
      const container = scrollContainerRef.current;
      const overlayRect = overlay.getBoundingClientRect();
      const selectionClientX = overlayRect.left + quickSearchRect.x;
      const selectionClientY = overlayRect.top + quickSearchRect.y;
      const selectionClientX2 = selectionClientX + quickSearchRect.width;
      const selectionClientY2 = selectionClientY + quickSearchRect.height;

      let dataUrl: string;

      if (pdfUrl) {
        // ---- PDF path: read directly from react-pdf's rendered <canvas> elements ----
        // react-pdf renders each page as a <canvas class="react-pdf__Page__canvas">
        // html2canvas cannot read these because they are tainted by the blob/remote PDF.
        // Instead, we crop directly from the canvas pixel buffer.
        const pageCanvases = Array.from(
          container.querySelectorAll<HTMLCanvasElement>('canvas.react-pdf__Page__canvas')
        );

        if (pageCanvases.length === 0) {
          throw new Error('No PDF page canvas found — pages may still be rendering.');
        }

        // Find which canvas(es) overlap the user selection
        const cropCanvas = document.createElement('canvas');
        const scale = window.devicePixelRatio || 1;
        cropCanvas.width = Math.max(1, Math.round(quickSearchRect.width * scale));
        cropCanvas.height = Math.max(1, Math.round(quickSearchRect.height * scale));
        const ctx = cropCanvas.getContext('2d')!;
        ctx.scale(scale, scale);

        let drew = false;
        for (const pageCanvas of pageCanvases) {
          const pr = pageCanvas.getBoundingClientRect();
          // Check if this canvas overlaps the selection at all
          if (pr.right < selectionClientX || pr.left > selectionClientX2) continue;
          if (pr.bottom < selectionClientY || pr.top > selectionClientY2) continue;

          // Pixel ratio: the canvas internal resolution vs its CSS size
          const canvasPixelRatioX = pageCanvas.width / pr.width;
          const canvasPixelRatioY = pageCanvas.height / pr.height;

          // Overlap region in client coords
          const overlapLeft = Math.max(selectionClientX, pr.left);
          const overlapTop = Math.max(selectionClientY, pr.top);
          const overlapRight = Math.min(selectionClientX2, pr.right);
          const overlapBottom = Math.min(selectionClientY2, pr.bottom);

          // Source region inside the canvas buffer
          const sx = (overlapLeft - pr.left) * canvasPixelRatioX;
          const sy = (overlapTop - pr.top) * canvasPixelRatioY;
          const sw = (overlapRight - overlapLeft) * canvasPixelRatioX;
          const sh = (overlapBottom - overlapTop) * canvasPixelRatioY;

          // Destination region inside our crop canvas (unscaled coords)
          const dx = overlapLeft - selectionClientX;
          const dy = overlapTop - selectionClientY;
          const dw = overlapRight - overlapLeft;
          const dh = overlapBottom - overlapTop;

          ctx.drawImage(pageCanvas, sx, sy, sw, sh, dx, dy, dw, dh);
          drew = true;
        }

        if (!drew) throw new Error('Selection did not overlap any rendered page.');
        dataUrl = cropCanvas.toDataURL('image/png');

      } else {
        // ---- HTML path: use html2canvas on the viewport to avoid scroll bugs ----
        // Hide the overlay during capture to avoid capturing the blue box itself
        const originalDisplay = overlay.style.display;
        overlay.style.display = 'none';

        // Patch getComputedStyle to bypass html2canvas oklch/oklab parsing bug
        const originalGetComputedStyle = window.getComputedStyle;
        window.getComputedStyle = function(el, pseudoElt) {
          const style = originalGetComputedStyle(el, pseudoElt);
          return new Proxy(style, {
            get(target, prop) {
              const val = target[prop as keyof CSSStyleDeclaration];
              if (typeof val === 'function') {
                return function(...args: any[]) {
                  const res = (val as Function).apply(target, args);
                  if (typeof res === 'string' && res.match(/(oklch|oklab|lch|lab|color)\(/)) return 'rgb(0,0,0)';
                  return res;
                };
              }
              if (typeof val === 'string' && val.match(/(oklch|oklab|lch|lab|color)\(/)) return 'rgb(0,0,0)';
              return val;
            }
          });
        };

        let fullCanvas;
        try {
          fullCanvas = await html2canvas(document.body, {
            background: '#ffffff',
            useCORS: true,
            logging: false,
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight,
            width: window.innerWidth,
            height: window.innerHeight,
            x: window.scrollX,
            y: window.scrollY,
            scale: Math.max(2, window.devicePixelRatio || 1),
          } as Parameters<typeof html2canvas>[1]);
        } finally {
          window.getComputedStyle = originalGetComputedStyle;
          overlay.style.display = originalDisplay;
        }

        const cropCanvas = document.createElement('canvas');
        const scale = Math.max(2, window.devicePixelRatio || 1);
        cropCanvas.width = quickSearchRect.width * scale;
        cropCanvas.height = quickSearchRect.height * scale;
        const ctx = cropCanvas.getContext('2d')!;
        
        ctx.drawImage(
            fullCanvas, 
            selectionClientX * scale, selectionClientY * scale, 
            quickSearchRect.width * scale, quickSearchRect.height * scale, 
            0, 0, 
            cropCanvas.width, cropCanvas.height
        );
        dataUrl = cropCanvas.toDataURL('image/png');
      }

      const returnState = {
        ...(location.state as any || {}),
        quickSearchRestore: {
          scrollTop: container.scrollTop,
          currentPage: pdfUrl ? currentPdfPage : currentPage,
          totalPageCount,
        },
      };

      navigate('/dashboard/utilities/search', {
        replace: true,
        state: {
          quickSearchImageDataUrl: dataUrl,
          quickSearchPrompt: `Analyze this cropped selection from ${subjectName || 'the lesson content'}. Identify any questions, text, or concepts present. Provide a clear explanation, solve any problems if applicable, and offer relevant context that would be helpful for a teacher. If the image is unclear, say what is missing and ask for a tighter crop. Finally, end your response by asking the user what else they would like to know more about regarding this topic or explanation.`,
          quickSearchSourceLabel: `${subjectName || 'Lesson content'} · page ${currentPage || currentPdfPage || 1}`,
          quickSearchReturn: {
            path: location.pathname,
            state: returnState,
          },
        },
      });
    } catch (error) {
      console.error('Quick search capture failed:', error);
      toast.error('Could not capture that area. Try a slightly larger box.');
      cancelQuickSearchCapture();
    }
  };

  const execEditorCommand = (command: string, value: string = '') => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    restoreSelection();
    if (command === 'foreColor' || command === 'backColor' || command === 'fontSize') {
      document.execCommand('styleWithCSS', false, 'true');
      document.execCommand(command === 'backColor' ? 'hiliteColor' : command, false, value === 'transparent' ? 'rgba(0,0,0,0)' : value);
      document.execCommand('styleWithCSS', false, 'false');
    } else {
      document.execCommand(command, false, value);
    }
    saveSelection();
    window.setTimeout(updateNoteFormatState, 0);
    if (editorRef.current) {
      const htmlContent = editorRef.current.innerHTML;
      setNotes(prev => ({ ...prev, [activeNoteKey]: htmlContent }));
    }
  };

  const clearNoteSelection = () => {
    const selection = window.getSelection();
    selection?.removeAllRanges();
    lastSelectionRangeRef.current = null;
    setActiveNoteFormats({
      bold: false,
      italic: false,
      underline: false,
      strikeThrough: false,
      insertUnorderedList: false,
      insertOrderedList: false,
      justifyLeft: false,
      justifyCenter: false,
      justifyRight: false,
    });
  };

  const runNoteToolbarCommand = (event: React.MouseEvent<HTMLButtonElement | HTMLSelectElement>, command: string, value = '') => {
    event.preventDefault();
    event.stopPropagation();
    execEditorCommand(command, value);
  };

  const handleNoteKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
      event.preventDefault();
      if (editorRef.current && !isPhotoNoteContent(notesRef.current[activeNoteKey])) {
        handleSaveNote(activeNoteKey, editorRef.current.innerHTML);
      }
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
      execEditorCommand('insertHTML', '&nbsp;&nbsp;&nbsp;&nbsp;');
    }
  };

  const handleNotePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const text = event.clipboardData.getData('text/plain');
    if (!text) return;
    event.preventDefault();
    execEditorCommand('insertText', text);
  };

  const noteToolButtonClass = (isActive = false, extra = '') => cn(
    "h-8 w-8 active:scale-95 rounded flex items-center justify-center text-sm transition-all",
    isActive
      ? "bg-blue-100 text-blue-700 ring-1 ring-blue-300 shadow-sm dark:bg-blue-500/20 dark:text-blue-200 dark:ring-blue-400/30"
      : "text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/10 hover:text-slate-950 dark:hover:text-white",
    extra
  );

  // ── Auto-scroll ──────────────────────────────────────
  const stopAutoScroll = React.useCallback(() => {
    isAutoScrollingRef.current = false;
    if (autoScrollRafRef.current !== null) { cancelAnimationFrame(autoScrollRafRef.current); autoScrollRafRef.current = null; }
    setIsAutoScrolling(false);
  }, []);

  const runAutoScrollFrame = React.useCallback((timestamp: number) => {
    if (!isAutoScrollingRef.current) return;
    const container = scrollContainerRef.current;
    if (!container) { stopAutoScroll(); return; }
    if (lastScrollTimeRef.current > 0) {
      const elapsed = timestamp - lastScrollTimeRef.current;
      const px = (autoScrollSpeedRef.current / 1000) * elapsed;
      container.scrollTop += px;
      if (container.scrollTop >= container.scrollHeight - container.clientHeight - 4) {
        stopAutoScroll(); return;
      }
    }
    lastScrollTimeRef.current = timestamp;
    autoScrollRafRef.current = requestAnimationFrame(runAutoScrollFrame);
  }, [stopAutoScroll]);

  const toggleAutoScroll = () => {
    if (isAutoScrolling) { stopAutoScroll(); return; }
    isAutoScrollingRef.current = true;
    lastScrollTimeRef.current = 0;
    setIsAutoScrolling(true);
    autoScrollRafRef.current = requestAnimationFrame(runAutoScrollFrame);
  };

  React.useEffect(() => { autoScrollSpeedRef.current = autoScrollSpeed; }, [autoScrollSpeed]);
  React.useEffect(() => () => stopAutoScroll(), [stopAutoScroll]);

  // ── Print handler ────────────────────────────────────
  const handlePrint = () => {
    // Single PDF view — open it directly in a new tab for the browser's native print
    if (pdfUrl && !isFullSubjectView) {
      window.open(pdfUrl, '_blank');
      return;
    }

    const win = window.open('', '_blank');
    if (!win) { toast.error('Popup blocked — allow popups for this site'); return; }

    const title = subjectName || contentTitle || 'Curriculum';
    let bodyHtml = `<h1 style="font-size:28px;font-weight:900;border-bottom:3px solid #4f46e5;padding-bottom:12px;margin-bottom:32px">${title}</h1>`;

    const pdfAttachments: { label: string; url: string }[] = [];

    if (isFullSubjectView && fullSyllabus?.topics && fullSubContentMap) {
      // Iterate syllabus data directly — never scrape the DOM / canvas elements
      fullSyllabus.topics.forEach((topic: any, ti: number) => {
        bodyHtml += `<div style="page-break-before:${ti > 0 ? 'always' : 'avoid'};margin-top:${ti > 0 ? '0' : '24px'}">
          <h2 style="font-size:20px;font-weight:800;color:#1e1b4b;border-left:4px solid #4f46e5;padding-left:12px;margin:0 0 20px">${ti + 1}. ${topic.title || 'Topic'}</h2>`;

        const topicData = fullSubContentMap[`${topic.id}_main`];
        if (!topic.subTopics?.length && topicData?.outlineContent) {
          bodyHtml += `<div style="margin-bottom:24px">${topicData.outlineContent}</div>`;
        }
        if (!topic.subTopics?.length && topicData?.referencePdfUrl) {
          pdfAttachments.push({ label: `${topic.title} — Reference PDF`, url: topicData.referencePdfUrl });
        }

        topic.subTopics?.forEach((sub: any, si: number) => {
          bodyHtml += `<h3 style="font-size:16px;font-weight:700;color:#312e81;margin:24px 0 12px">${ti + 1}.${si + 1} ${sub.title || 'Sub-topic'}</h3>`;
          const subData = getCanonicalSubTopicContent(topic, sub, si);
          if (subData?.outlineContent) {
            bodyHtml += `<div style="margin-bottom:20px">${subData.outlineContent}</div>`;
          }
          if (subData?.referencePdfUrl) {
            pdfAttachments.push({ label: `${topic.title} › ${sub.title} — Reference PDF`, url: subData.referencePdfUrl });
          }
        });

        bodyHtml += `</div>`;
      });
    } else if (htmlContent) {
      bodyHtml += `<div>${htmlContent}</div>`;
    }

    // List PDF attachments as clickable links — can't render them inline without a heavy lib
    if (pdfAttachments.length > 0) {
      bodyHtml += `<div style="page-break-before:always;margin-top:0">
        <h2 style="font-size:18px;font-weight:800;color:#1e1b4b;border-left:4px solid #4f46e5;padding-left:12px;margin:0 0 20px">PDF Attachments</h2>
        <p style="color:#64748b;font-size:13px;margin-bottom:16px">These PDF documents are attached to this curriculum. Open each link to view or print separately.</p>
        <ul style="list-style:none;padding:0;margin:0">`;
      pdfAttachments.forEach(att => {
        bodyHtml += `<li style="margin-bottom:12px;padding:12px 16px;border:1px solid #e2e8f0;border-radius:8px">
          <a href="${att.url}" target="_blank" style="color:#4f46e5;font-weight:600;text-decoration:none">${att.label}</a>
          <span style="display:block;color:#94a3b8;font-size:11px;margin-top:2px">Click to open PDF in a new tab</span>
        </li>`;
      });
      bodyHtml += `</ul></div>`;
    }

    win.document.write(`<!DOCTYPE html><html><head><title>${title}</title><style>
      *, *::before, *::after { box-sizing: border-box; }
      body { font-family: Georgia, 'Times New Roman', serif; max-width: 820px; margin: 40px auto; color: #1a1a1a; line-height: 1.75; font-size: 15px; padding: 0 20px; }
      h1, h2, h3, h4 { font-family: system-ui, -apple-system, sans-serif; }
      h2 { margin-top: 0; } h3 { color: #374151; }
      p { margin: 8px 0; } ul, ol { padding-left: 24px; margin: 8px 0; } li { margin-bottom: 4px; }
      table { width: 100%; border-collapse: collapse; margin: 16px 0; }
      th, td { border: 1px solid #d1d5db; padding: 8px 12px; text-align: left; }
      th { background: #f8fafc; font-weight: 700; }
      blockquote { border-left: 3px solid #4f46e5; margin: 16px 0; padding: 8px 16px; color: #475569; background: #f8fafc; }
      code { background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-size: 13px; }
      pre { background: #1e293b; color: #e2e8f0; padding: 16px; border-radius: 8px; overflow-x: auto; }
      img { max-width: 100%; height: auto; }
      @media print { body { margin: 0; padding: 20px; } a { color: #4f46e5 !important; } }
    </style></head><body>${bodyHtml}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 600);
  };

  if (waitingForReset) {
    // Show loader until progress is confirmed reset
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] w-full">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4" />
        <div className="text-indigo-500 font-bold text-lg">Resetting progress...</div>
      </div>
    );
  }

  return (
    <div className={cn(
      "w-full flex flex-col transition-all duration-700 relative",
      isFullscreenMode && "fixed inset-0 z-[9999]",
      hasReadableContent
        ? (isEmbedded || directPdfUrl ? "h-full bg-white dark:bg-[#020617]" : "h-screen pb-0 bg-white dark:bg-[#020617]")
        : (isEmbedded ? "h-full py-4 px-2 space-y-4" : "max-w-5xl mx-auto py-6 sm:py-8 px-3 sm:px-4 space-y-6 sm:space-y-8 h-auto")
    )}>
      {isRestoringScroll && (
        <div className="fixed bottom-8 right-8 z-[99999] flex items-center gap-3 bg-slate-900/90 dark:bg-black/90 backdrop-blur-md px-5 py-3.5 rounded-full shadow-2xl border border-white/10 text-white shadow-indigo-500/20">
           <Loader2 className="h-5 w-5 text-indigo-400 animate-spin" />
           <p className="text-sm font-semibold tracking-wide">Restoring your page...</p>
        </div>
      )}
      {/* HEADER ACTION BAR */}
      {!directPdfUrl && (
        <header className={cn(
        "flex flex-wrap items-center justify-between gap-3 shrink-0 transition-all duration-500",
        hasReadableContent && "px-3 sm:px-6 lg:px-8 py-4 sm:py-5 bg-white/95 dark:bg-[#020617]/95 backdrop-blur-3xl border-b border-gray-200 dark:border-white/5 shadow-2xl z-50"
      )}>
        <div className="flex items-center gap-3 sm:gap-5 min-w-0 flex-1">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleBack} 
            className="h-9 w-9 sm:h-10 sm:w-10 shrink-0 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all shadow-sm"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 min-w-0">
              <h1 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tighter truncate">
                {resolvedHeaderTitle || ((pdfUrl || htmlContent) ? "Pedagogical Workspace" : "Content Canvas")}
              </h1>
            </div>

          </div>
        </div>

        {/* Zoom controls — view only, no upload/edit buttons. Single set, lives in the
            header. The "Registry" and "Doc Mode" pills floating over the canvas are
            now just labels (no zoom/fullscreen duplication). */}
        {hasReadableContent && (
          <div className="flex flex-wrap items-center justify-end gap-1.5 sm:gap-2">
            <div className="flex items-center gap-0.5 sm:gap-1 bg-gray-100 dark:bg-white/5 p-0.5 sm:p-1 rounded-lg sm:rounded-xl border border-gray-200 dark:border-white/10">
              <Button
                variant="ghost"
                size="icon"
                disabled={zoom <= 0.4}
                onClick={() => setZoom(prev => Math.max(0.4, prev - 0.1))}
                className="h-7 w-7 rounded-md hover:bg-gray-200 dark:hover:bg-white/10 text-slate-500 dark:text-slate-400 active:scale-90 transition-all"
                aria-label="Zoom out"
              >
                <ZoomOut className="h-3.5 w-3.5" />
              </Button>
              <span className="text-[9px] font-black text-slate-500 w-8 text-center uppercase tracking-tighter tabular-nums">
                {Math.round(zoom * 100)}%
              </span>
              <Button
                variant="ghost"
                size="icon"
                disabled={zoom >= 3.0}
                onClick={() => setZoom(prev => Math.min(3.0, prev + 0.1))}
                className="h-7 w-7 rounded-md hover:bg-gray-200 dark:hover:bg-white/10 text-slate-500 dark:text-slate-400 active:scale-90 transition-all"
                aria-label="Zoom in"
              >
                <ZoomIn className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 bg-gray-100 dark:bg-white/5 px-1.5 sm:px-3 py-1 sm:py-1.5 rounded-lg sm:rounded-xl border border-gray-200 dark:border-white/10">
              <Eye className="h-3.5 w-3.5 text-amber-600 dark:text-amber-300 shrink-0" />
              <Slider
                value={[eyeCareStrength]}
                onValueChange={(value) => setEyeCareStrength(value[0] ?? 0)}
                min={0}
                max={100}
                step={5}
                className="w-14 sm:w-24 md:w-28"
                aria-label="Eye care strength"
              />
              <span className="hidden sm:inline-block w-7 text-right text-[9px] font-black text-amber-600/80 dark:text-amber-200/70 tabular-nums">{eyeCareStrength}%</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsFullscreenMode(prev => !prev)}
              className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg sm:rounded-xl bg-gray-100 dark:bg-white/5 hover:bg-indigo-600 text-slate-600 dark:text-slate-300 hover:text-white border border-gray-200 dark:border-white/10 transition-all active:scale-95"
              aria-label={isFullscreenMode ? 'Exit fullscreen mode' : 'Enter fullscreen mode'}
            >
              {isFullscreenMode ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>

            {hasReadableContent && docType === 'subtopic' && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard/lessons/course/outline', {
                  state: {
                    subjectId,
                    subjectName,
                    isFirebaseSubject,
                    docType: 'subtopic',
                    topicId,
                    subTopicId,
                    subTopicTitle: contentTitle,
                    activeItemId: (location.state as any)?.activeItemId || null,
                    fromPath: '/dashboard/lessons/course/content',
                    returnPath: '/dashboard/lessons/course/content'
                  }
                })}
                title="Manage content (add PDFs, outlines, etc.)"
                className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg sm:rounded-xl bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-500 text-emerald-500 dark:text-emerald-300 hover:text-white border border-emerald-200 dark:border-emerald-500/20 transition-all active:scale-95"
                aria-label="Open content manager"
              >
                <Edit3 className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </header>
      )}

      {/* ═══ CONTENT-AREA LOADER ═══ */}
      {/* Shown briefly while the content (PDF or HTML) is being hydrated
          from the route state. Mirrors the loader pattern in
          CourseOutlineEditor — covers the content area so the user never
          sees a flash of the previous subject's content or an empty state. */}
      {isLoadingContent && (
        <div
          role="status"
          aria-live="polite"
          aria-label="Loading course content"
          className="min-h-[60vh] flex flex-col items-center justify-center gap-5 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800 bg-white/40 dark:bg-slate-900/40 backdrop-blur-sm"
        >
          <div className="relative h-14 w-14">
            <div className="absolute inset-0 rounded-full border-2 border-indigo-200 dark:border-indigo-900/40" />
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-indigo-500 dark:border-t-indigo-400 animate-spin" />
            <FileText className="absolute inset-0 m-auto h-5 w-5 text-indigo-500" />
          </div>
          <div className="text-center space-y-1.5">
            <p className="text-sm font-bold text-slate-900 dark:text-white">Restoring your course…</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xs">
              {resolvedHeaderTitle
                ? <>Pulling <span className="font-semibold text-slate-700 dark:text-slate-200">{resolvedHeaderTitle}</span> from your workspace.</>
                : 'Pulling the latest syllabus, topics, and sub-topic content from your workspace.'}
            </p>
          </div>
        </div>
      )}

      {/* CONTENT AREA */}
      {!isLoadingContent && (
      <div className={cn(
        "flex-1 w-full relative overflow-hidden flex",
        showNoteTakerSplit ? "flex-row bg-slate-100 dark:bg-[#121212]" : "flex-col",
        !hasReadableContent && "min-h-[600px] px-4"
      )}>
        {/* Left Pane (Main Viewer Container) — hidden when note taker is fullscreen or on mobile when notes open */}
        <div className={cn(
          "flex-1 h-full flex flex-col relative overflow-hidden transition-all duration-300",
          mobileContainerWidth < 768 && showNoteTakerSplit && "hidden",
          mobileContainerWidth >= 768 && showNoteTakerSplit && !noteTakerFullscreen && (notePaneWidth === 'wide' ? "w-[50%] flex-none shrink-0" : "w-[60%] flex-none shrink-0"),
          mobileContainerWidth >= 768 && showNoteTakerSplit && noteTakerFullscreen && "hidden"
        )}>
          <AnimatePresence mode="wait">
          {isFullSubjectView && fullSyllabus ? (
            <motion.div 
              key="full-subject-viewer"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex flex-col h-full bg-white dark:bg-[#020617] relative overflow-hidden"
            >
              {resumeProgressEnabled && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-white/5 z-[200]">
                  <div 
                     className="h-full bg-indigo-500 rounded-r-full shadow-[0_0_10px_rgba(99,102,241,0.5)] transition-all duration-150 ease-out" 
                     style={{ width: `${scrollProgress}%` }}
                  />
                </div>
              )}

              {/* TOPIC FILTER ACTIVE INDICATOR */}
              {topicFilter !== 'all' && (
                <div className="absolute top-[4.5rem] left-6 z-[150] flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600/20 border border-indigo-500/30 rounded-full backdrop-blur-3xl">
                  <Filter className="h-3 w-3 text-indigo-400" />
                  <span className="text-[9px] font-black text-indigo-300 uppercase tracking-widest max-w-[120px] truncate">
                    {fullSyllabus?.topics?.find((t: any) => t.id === topicFilter)?.title || 'Filtered'}
                  </span>
                  <button onClick={() => setTopicFilter('all')} title="Clear topic filter" className="text-indigo-500/60 hover:text-indigo-300 ml-0.5 transition-colors">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}

              {/* READER CONTROLS OVERLAY — right side (compact bar + expandable tools panel) */}
              <div className="absolute top-4 right-3 sm:top-6 sm:right-6 z-[100] flex flex-col items-end gap-0 max-w-[calc(100vw-1.5rem)]">
                {/* ── Compact control bar ── */}
                <div className="flex items-center gap-0 bg-white/85 dark:bg-[#020617]/85 backdrop-blur-3xl rounded-xl border border-gray-200 dark:border-white/8 shadow-2xl overflow-x-auto no-scrollbar max-w-full divide-x divide-gray-200 dark:divide-white/8">
                   {/* Label */}
                   <div className="flex items-center gap-1.5 px-3 py-2 text-[9px] font-black uppercase tracking-widest shrink-0">
                      <BookOpen className="h-3 w-3 text-indigo-500" />
                      {!resumeProgressEnabled ? (
                        <span className="text-indigo-600 dark:text-indigo-400">Preview</span>
                      ) : isCompleted ? (
                        <span className="text-emerald-600 dark:text-emerald-400">Completed</span>
                      ) : (
                        <><span className="text-indigo-600 dark:text-indigo-400">{Math.round(scrollProgress)}%</span><span className="text-slate-400 dark:text-white/25"> read</span></>
                      )}
                   </div>
                   {/* Zoom */}
                   <div className="flex items-center gap-0.5 px-2 py-1.5">
                     <Button variant="ghost" size="icon" disabled={zoom <= 0.4} onClick={() => setZoom(p => Math.max(0.4, p - 0.1))} className="h-6 w-6 rounded hover:bg-gray-200 dark:hover:bg-white/10 text-slate-500 hover:text-black dark:hover:text-white">
                       <ZoomOut className="h-3 w-3" />
                     </Button>
                     <span className="text-[9px] font-black text-slate-500 w-7 text-center">{Math.round(zoom * 100)}%</span>
                     <Button variant="ghost" size="icon" disabled={zoom >= 3.0} onClick={() => setZoom(p => Math.min(3.0, p + 0.1))} className="h-6 w-6 rounded hover:bg-gray-200 dark:hover:bg-white/10 text-slate-500 hover:text-black dark:hover:text-white">
                       <ZoomIn className="h-3 w-3" />
                     </Button>
                   </div>
                   {/* Page counter */}
                   <div className="flex items-center gap-1 px-2.5 py-1.5">
                     <Hash className="h-3 w-3 text-slate-400 dark:text-white/20 shrink-0" />
                     {totalPageCount > 0 ? (
                       <span className="text-[9px] font-black text-indigo-600 dark:text-indigo-300 shrink-0">
                         {currentPage}<span className="text-slate-400 dark:text-white/20 font-medium">/{totalPageCount}</span>
                       </span>
                     ) : (
                       <span className="text-[9px] text-slate-400 dark:text-white/20 shrink-0">--/--</span>
                     )}
                   </div>
                   {/* Auto-scroll indicator */}
                   {isAutoScrolling && (
                     <div className="flex items-center gap-1 px-2 py-1.5">
                       <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                       <span className="text-[9px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest shrink-0">Auto</span>
                     </div>
                   )}

                   {/* Tools toggle */}
                   <button
                     onClick={() => setShowToolsPanel(v => !v)}
                     className={cn(
                       "flex items-center gap-1.5 px-3 py-2 text-[9px] font-black uppercase tracking-widest transition-all shrink-0",
                       showToolsPanel ? "bg-indigo-600/25 text-indigo-600 dark:text-indigo-300" : "text-slate-400 dark:text-white/40 hover:text-slate-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5"
                     )}
                   >
                     <SlidersHorizontal className="h-3.5 w-3.5" />
                     <span className="hidden sm:inline">Tools</span>
                     <ChevronDown className={cn("h-3 w-3 transition-transform duration-200", showToolsPanel && "rotate-180")} />
                   </button>
                </div>

                {/* ── Expandable tools panel ── */}
                {showToolsPanel && (
                  <div className="mt-1.5 w-[calc(100vw-1.5rem)] sm:w-80 max-h-[70vh] overflow-y-auto overflow-x-hidden custom-scrollbar bg-white/96 dark:bg-[#020617]/96 border border-gray-200 dark:border-white/10 rounded-xl shadow-2xl backdrop-blur-3xl">
                    {/* Section: Navigation */}
                    <div className="border-b border-gray-100 dark:border-white/5">
                      <button onClick={() => setActiveToolSection(s => s === 'navigation' ? null : 'navigation')}
                        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-white/3 transition-colors group">
                        <div className="flex items-center gap-2">
                          <List className="h-3.5 w-3.5 text-indigo-500" />
                          <span className="text-[10px] font-black text-slate-700 dark:text-white/70 uppercase tracking-widest">Navigation</span>
                          {topicFilter !== 'all' && <span className="text-[8px] font-black text-indigo-400 bg-indigo-600/20 px-1.5 py-0.5 rounded-full">Filtered</span>}
                        </div>
                        <ChevronRight className={cn("h-3 w-3 text-slate-300 dark:text-white/20 transition-transform duration-200", activeToolSection === 'navigation' && "rotate-90")} />
                      </button>
                      {activeToolSection === 'navigation' && (
                        <div className="pb-1">
                          {topicFilter !== 'all' && (
                            <button onClick={() => setTopicFilter('all')} className="w-full flex items-center gap-1.5 px-4 py-1.5 text-[9px] text-indigo-500 hover:text-indigo-600 font-black uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-white/3 transition-colors">
                              <X className="h-3 w-3" /> Clear topic filter
                            </button>
                          )}
                          <button onClick={() => { scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); setTopicFilter('all'); setShowToolsPanel(false); }}
                            className="w-full text-left px-4 py-1.5 text-[9px] text-slate-400 dark:text-white/40 hover:text-slate-700 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/3 transition-colors font-black uppercase tracking-widest border-b border-gray-100 dark:border-white/5">
                            ↑ Back to top (All Topics)
                          </button>
                          <div className="max-h-52 overflow-y-auto custom-scrollbar">
                            {fullSyllabus?.topics?.map((topic: any, tIdx: number) => (
                              <React.Fragment key={topic.id}>
                                <div className="flex items-stretch">
                                  <button onClick={() => { scrollToTopicId(topic.id); setShowToolsPanel(false); }}
                                    className="flex-1 text-left px-4 py-2 text-[10px] font-semibold text-slate-600 dark:text-white/70 hover:text-slate-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/4 transition-colors truncate">
                                    <span className="text-indigo-500/60 mr-1.5 font-black text-[8px]">T{tIdx + 1}</span>{topic.title}
                                  </button>
                                  <button onClick={() => { setTopicFilter(topicFilter === topic.id ? 'all' : topic.id); setShowToolsPanel(false); }}
                                    title={topicFilter === topic.id ? 'Remove filter' : 'Show only this topic'}
                                    className={cn("px-2.5 border-l border-gray-100 dark:border-white/5 transition-colors flex items-center shrink-0",
                                      topicFilter === topic.id ? "text-indigo-400 bg-indigo-600/20" : "text-slate-300 dark:text-white/15 hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-white/5")}>
                                    <Filter className="h-3 w-3" />
                                  </button>
                                </div>
                                {topic.subTopics?.map((sub: any, sIdx: number) => (
                                  <button key={sub.id} onClick={() => { scrollToSubTopicId(sub.id); setShowToolsPanel(false); }}
                                    className="w-full text-left pl-8 pr-3 py-1 text-[9px] text-slate-400 dark:text-white/35 hover:text-slate-700 dark:hover:text-white/70 hover:bg-gray-50 dark:hover:bg-white/4 transition-colors flex items-center gap-2 truncate">
                                    <span className="text-indigo-600/50 font-black text-[8px] shrink-0">{tIdx + 1}.{sIdx + 1}</span>{sub.title}
                                  </button>
                                ))}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Section: Bookmarks */}
                    <div className="border-b border-gray-100 dark:border-white/5">
                      <button onClick={() => setActiveToolSection(s => s === 'bookmarks' ? null : 'bookmarks')}
                        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-white/3 transition-colors">
                        <div className="flex items-center gap-2">
                          <Bookmark className="h-3.5 w-3.5 text-amber-500" />
                          <span className="text-[10px] font-black text-slate-700 dark:text-white/70 uppercase tracking-widest">Bookmarks</span>
                          {bookmarks.length > 0 && <span className="text-[8px] font-black text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/10 px-1.5 py-0.5 rounded-full">{bookmarks.length}</span>}
                        </div>
                        <ChevronRight className={cn("h-3 w-3 text-slate-300 dark:text-white/20 transition-transform duration-200", activeToolSection === 'bookmarks' && "rotate-90")} />
                      </button>
                      {activeToolSection === 'bookmarks' && (
                        <div className="pb-2">
                          <button onClick={handleAddBookmark} disabled={isSavingBookmark}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-[9px] font-black uppercase tracking-widest text-amber-600 dark:text-amber-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/5 border-b border-gray-100 dark:border-white/5 transition-colors disabled:opacity-50">
                            {isSavingBookmark ? <><div className="h-3 w-3 border border-amber-400/40 border-t-amber-400 rounded-full animate-spin" /> Saving...</> : <><BookmarkCheck className="h-3.5 w-3.5" /> Bookmark current position</>}
                          </button>
                          {bookmarks.length === 0 ? (
                            <p className="text-center text-[9px] text-slate-400 dark:text-white/20 font-medium px-4 py-4">No bookmarks yet. Scroll to any position and save it.</p>
                          ) : (
                              <div className="max-h-44 overflow-y-auto custom-scrollbar py-1">
                               {bookmarks.map(bm => (
                                 <div key={bm.id} className="flex items-center hover:bg-gray-50 dark:hover:bg-white/3 transition-colors group/bm">
                                   <button onClick={() => { jumpToBookmark(bm); setShowToolsPanel(false); }}
                                     className="flex-1 text-left px-4 py-2 min-w-0">
                                     <div className="text-[9px] font-bold text-slate-500 dark:text-white/60 truncate group-hover/bm:text-slate-700 dark:group-hover/bm:text-white/80">{bm.label}</div>
                                   </button>
                                   <button onClick={() => handleDeleteBookmark(bm.id)} title="Delete bookmark"
                                     className="px-2.5 text-slate-300 dark:text-white/15 hover:text-red-400 transition-colors shrink-0 opacity-0 group-hover/bm:opacity-100">
                                     <Trash2 className="h-3 w-3" />
                                   </button>
                                 </div>
                               ))}
                             </div>
                           )}
                         </div>
                       )}
                     </div>

                    {/* Section: Reading Tools */}
                    <div className="border-b border-gray-100 dark:border-white/5">
                      <button onClick={() => setActiveToolSection(s => s === 'reading' ? null : 'reading')}
                        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-white/3 transition-colors">
                        <div className="flex items-center gap-2">
                          <Clock className="h-3.5 w-3.5 text-violet-500 dark:text-violet-400" />
                          <span className="text-[10px] font-black text-slate-700 dark:text-white/70 uppercase tracking-widest">Reading Tools</span>
                          {readingTimeMin && <span className="text-[8px] text-violet-600/70 dark:text-violet-400/70 font-medium">~{readingTimeMin} min</span>}
                        </div>
                        <ChevronRight className={cn("h-3 w-3 text-slate-300 dark:text-white/20 transition-transform duration-200", activeToolSection === 'reading' && "rotate-90")} />
                      </button>
                      {activeToolSection === 'reading' && (
                        <div className="px-4 pb-4 space-y-3">
                          {readingTimeMin && (
                            <div className="flex items-center gap-3 p-2.5 rounded-lg bg-violet-50 dark:bg-violet-500/5 border border-violet-200 dark:border-violet-500/10">
                              <Clock className="h-4 w-4 text-violet-500 dark:text-violet-400 shrink-0" />
                              <div>
                                <div className="text-[10px] font-black text-slate-500 dark:text-white/60 uppercase tracking-widest">Estimated read time</div>
                                <div className="text-lg font-black text-violet-600 dark:text-violet-300">{readingTimeMin} <span className="text-[10px] text-slate-400 dark:text-white/30 font-medium">minutes</span></div>
                              </div>
                            </div>
                          )}
                          {/* Auto-scroll */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-[9px] font-black text-slate-400 dark:text-white/40 uppercase tracking-widest">Auto-scroll</span>
                              <button onClick={toggleAutoScroll}
                                className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all active:scale-95",
                                  isAutoScrolling ? "bg-emerald-100 dark:bg-emerald-600/20 text-emerald-600 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-500/20 hover:bg-emerald-200 dark:hover:bg-emerald-600/30" : "bg-gray-100 dark:bg-white/5 text-slate-500 dark:text-white/50 border border-gray-200 dark:border-white/10 hover:bg-gray-200 dark:hover:bg-white/8 hover:text-slate-700 dark:hover:text-white")}>
                                {isAutoScrolling ? <><Pause className="h-3 w-3" /> Pause</> : <><Play className="h-3 w-3" /> Start</>}
                              </button>
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center justify-between text-[9px] text-slate-400 dark:text-white/25 font-medium">
                                <span>Speed</span>
                                <span className="text-slate-500 dark:text-white/40 font-black">{autoScrollSpeed} px/s</span>
                              </div>
                              <input type="range" min="5" max="100" step="5" value={autoScrollSpeed}
                                onChange={e => setAutoScrollSpeed(Number(e.target.value))}
                                className="w-full accent-indigo-500 h-1 rounded-full cursor-pointer" />
                              <div className="flex justify-between text-[8px] text-slate-300 dark:text-white/15"><span>Slow</span><span>Fast</span></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Section: Export */}
                    <div>
                      <button onClick={() => setActiveToolSection(s => s === 'export' ? null : 'export')}
                        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-white/3 transition-colors">
                        <div className="flex items-center gap-2">
                          <Printer className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" />
                          <span className="text-[10px] font-black text-slate-700 dark:text-white/70 uppercase tracking-widest">Export</span>
                        </div>
                        <ChevronRight className={cn("h-3 w-3 text-slate-300 dark:text-white/20 transition-transform duration-200", activeToolSection === 'export' && "rotate-90")} />
                      </button>
                      {activeToolSection === 'export' && (
                        <div className="px-4 pb-4 space-y-2">
                          <button onClick={handlePrint}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-gray-50 dark:bg-white/4 border border-gray-200 dark:border-white/8 hover:bg-gray-100 dark:hover:bg-white/7 hover:border-gray-300 dark:hover:border-white/15 transition-all active:scale-[0.98] text-left">
                            <Printer className="h-4 w-4 text-slate-500 dark:text-slate-400 shrink-0" />
                            <div>
                              <div className="text-[10px] font-black text-slate-700 dark:text-white/70 uppercase tracking-widest">Print / Save as PDF</div>
                              <div className="text-[9px] text-slate-400 dark:text-white/30 font-medium mt-0.5">Opens print dialog in new window</div>
                            </div>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Section: Quick Search */}
                    <div className="border-b border-gray-100 dark:border-white/5">
                      <button onClick={startQuickSearchCapture}
                        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-blue-500/5 transition-colors">
                        <div className="flex items-center gap-2">
                          <Search className="h-3.5 w-3.5 text-blue-500" />
                          <span className="text-[10px] font-black text-slate-700 dark:text-white/70 uppercase tracking-widest">Quick Search</span>
                        </div>
                        <ChevronRight className="h-3 w-3 text-slate-300 dark:text-white/20" />
                      </button>
                    </div>

                    {/* Section: Take Notes */}
                    <div>
                      <button onClick={() => { setShowNoteTakerSplit(true); setShowToolsPanel(false); }}
                        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-emerald-50 dark:hover:bg-emerald-500/5 transition-colors">
                        <div className="flex items-center gap-2">
                          <Edit className="h-3.5 w-3.5 text-emerald-500" />
                          <span className="text-[10px] font-black text-slate-700 dark:text-white/70 uppercase tracking-widest">Take Notes</span>
                        </div>
                        <ChevronRight className="h-3 w-3 text-slate-300 dark:text-white/20" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

                {/* Wrapper gives us a stacking context for the restore overlay */}
                <div className="relative flex-1 min-h-0 flex flex-col">

                {/* ── Resume Restore Overlay ── */}
                {/* Covers the read area while the stable-layout detector is repositioning */}
                {isRestoringProgress && (
                  <div
                    className="absolute inset-0 z-[500] flex flex-col items-center justify-center gap-6 bg-slate-100 dark:bg-[#0f172a] transition-opacity duration-300"
                    style={{ backdropFilter: 'blur(4px)' }}
                  >
                    {/* Animated ring */}
                    <div className="relative flex items-center justify-center">
                      <div className="h-20 w-20 rounded-full border-2 border-indigo-500/15 border-t-indigo-500 animate-spin" />
                      <div className="absolute h-12 w-12 rounded-full border-2 border-violet-400/20 border-b-violet-400 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.8s' }} />
                      <BookOpen className="absolute h-5 w-5 text-indigo-400" />
                    </div>
                    {/* Label */}
                    <div className="flex flex-col items-center gap-2 text-center">
                      <p className="text-sm font-black uppercase tracking-[0.25em] text-slate-600 dark:text-white/60">
                        Resuming your session
                      </p>
                      <p className="text-xs text-slate-400 dark:text-white/30 font-medium">
                        Loading to page {restoreTarget.currentPage} · {Math.round(restoreTarget.progressPercent)}% read
                      </p>
                    </div>
                    {/* Progress track */}
                    <div className="w-48 h-0.5 bg-slate-200 dark:bg-white/8 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full animate-pulse"
                        style={{ width: `${Math.min(100, restoreTarget.progressPercent)}%` }}
                      />
                    </div>
                  </div>
                )}

                <div
                  ref={scrollRefCallback}
                  className="reader-scroll-area flex-1 overflow-y-auto p-3 sm:p-6 lg:p-12 custom-scrollbar bg-slate-100 dark:bg-[#0f172a] overflow-x-auto"
                  onScroll={handleScroll}
                  style={eyeCareStyle}
                >
                  <div
                    className="flex flex-col items-center pt-8 pb-40 gap-8"
                    style={{ transform: `scale(${zoom})`, transformOrigin: 'top center', transition: 'transform 0.2s cubic-bezier(0.2, 0, 0, 1)' }}
                  >
                  {/* DOCUMENT START: COURSE OUTLINE */}
                  <div className="flex flex-col items-center gap-8 w-full">
                    {/* Render Course Outline PDF first if it exists */}
                    {fullSyllabus.referencePdfUrl && (
                      <SyllabusDocumentSection
                        file={fullSyllabus.referencePdfUrl}
                        zoom={zoom}
                        title="MASTER SYLLABUS OUTLINE"
                        pageFitScale={coursePageFitScale}
                        isQuickSearchSelecting={isQuickSearchSelecting}
                        quickSearchOverlayRef={quickSearchOverlayRef}
                        handleQuickSearchMouseDown={handleQuickSearchMouseDown}
                        handleQuickSearchMouseMove={handleQuickSearchMouseMove}
                        finishQuickSearchCapture={finishQuickSearchCapture}
                        handleQuickSearchTouchEnd={handleQuickSearchTouchEnd}
                        isQuickSearchCapturing={isQuickSearchCapturing}
                        cancelQuickSearchCapture={cancelQuickSearchCapture}
                        quickSearchRect={quickSearchRect}
                      />
                    )}

                    {/* Only show paper-sheet if there is HTML content and NO PDF (to avoid duplication) */}
                    {!fullSyllabus.referencePdfUrl && (
                      fullSyllabus.outlineContent ? (
                        // HTML outline — not absolutely positioned so content can grow freely
                        <div data-progress-page="true" className="course-reader-page-slot w-full" style={{ maxWidth: COURSE_PAGE_WIDTH * coursePageFitScale }}>
                          <div
                            className="course-reader-page paper-sheet tiptap-editor-area bg-white"
                            style={{
                              width: COURSE_PAGE_WIDTH,
                              minHeight: COURSE_PAGE_HEIGHT,
                              border: '1px solid #cbd5e1',
                              boxSizing: 'border-box',
                              boxShadow: '0 8px 32px 0 rgba(0,0,0,0.18), 0 1.5px 4px 0 rgba(0,0,0,0.10)',
                              zoom: coursePageFitScale,
                            }}
                          >
                            <div
                               className="tiptap prose prose-slate max-w-none text-slate-900 overflow-hidden"
                               style={{ padding: mobileContainerWidth < 640 ? '96px' : '32px', minHeight: '11in' }}
                               dangerouslySetInnerHTML={{ __html: fullSyllabus.outlineContent }}
                            />
                          </div>
                        </div>
                      ) : !fullSubContentMap ? (
                        /* Skeleton while data loads */
                        <div data-progress-page="true" className="course-reader-page-slot relative overflow-hidden" style={{ width: COURSE_PAGE_WIDTH * coursePageFitScale, height: COURSE_PAGE_HEIGHT * coursePageFitScale }}>
                          <div
                            className="absolute left-0 top-0 bg-white border border-slate-200"
                            style={{
                              width: COURSE_PAGE_WIDTH,
                              height: COURSE_PAGE_HEIGHT,
                              transform: `scale(${coursePageFitScale})`,
                              transformOrigin: 'top left',
                              boxShadow: '0 8px 32px 0 rgba(0,0,0,0.14)',
                            }}
                          >
                            <div className="h-full w-full animate-pulse bg-gradient-to-b from-slate-50 via-white to-slate-50 flex flex-col gap-5 p-24">
                              <div className="h-5 bg-slate-200 rounded-full w-1/2 mb-4" />
                              {[...Array(8)].map((_,i) => <div key={i} className={`h-3 bg-slate-100 rounded-full ${i % 3 === 2 ? 'w-3/4' : 'w-full'}`} />)}
                              <div className="h-4 bg-slate-200 rounded-full w-1/3 mt-6" />
                              {[...Array(5)].map((_,i) => <div key={i} className={`h-3 bg-slate-100 rounded-full ${i % 2 === 1 ? 'w-5/6' : 'w-full'}`} />)}
                            </div>
                          </div>
                        </div>
                      ) : null
                    )}
                  </div>
                  {/* SEQUENTIAL TOPICS & SUB-TOPICS */}
                  {fullSyllabus.topics?.map((topic: any, tIndex: number) => {
                    // Apply topic filter — null return skips rendering; original tIndex preserved for correct numbering
                    if (topicFilter !== 'all' && topic.id !== topicFilter) return null;
                    const topicData = topic.subTopics?.length ? null : fullSubContentMap?.[`${topic.id}_main`];
                    const topicNum = tIndex + 1;
                    const isLastTopic = tIndex === (fullSyllabus.topics?.length ?? 0) - 1;
                    return (
                      <React.Fragment key={topic.id}>
                         {/* TOPIC SEPARATION ANCHOR */}
                         <div data-topic-id={topic.id} className="w-full flex items-center gap-6 mt-6 mb-4 animate-in slide-in-from-left duration-700">
                            <div className="h-0.5 flex-1 bg-slate-900/10 dark:bg-white/10" />
                            <div className="flex flex-col items-center gap-0.5 px-4">
                               <div className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest leading-none">Topic {topicNum}</div>
                               <h2 className="text-2xl font-extrabold text-slate-900 dark:text-white tracking-tight uppercase leading-tight">{topic.title}</h2>
                            </div>
                            <div className="h-0.5 flex-1 bg-slate-900/10 dark:bg-white/10" />
                         </div>

                         {/* Topic Overview Content (PDF preferred) */}
                         {topicData?.referencePdfUrl && (
                            <SyllabusDocumentSection
                              file={topicData.referencePdfUrl}
                              zoom={zoom}
                              pageFitScale={coursePageFitScale}
                              isQuickSearchSelecting={isQuickSearchSelecting}
                              quickSearchOverlayRef={quickSearchOverlayRef}
                              handleQuickSearchMouseDown={handleQuickSearchMouseDown}
                              handleQuickSearchMouseMove={handleQuickSearchMouseMove}
                              finishQuickSearchCapture={finishQuickSearchCapture}
                              isQuickSearchCapturing={isQuickSearchCapturing}
                              cancelQuickSearchCapture={cancelQuickSearchCapture}
                              quickSearchRect={quickSearchRect}
                            />
                         )}
                         {topicData?.referencePdfUrl ? null : topicData?.outlineContent ? (
                           // HTML topic content — in-flow so height grows naturally
                           <div data-progress-page="true" className="course-reader-page-slot w-full" style={{ maxWidth: COURSE_PAGE_WIDTH * coursePageFitScale }}>
                             <div
                               className="course-reader-page paper-sheet bg-white"
                               style={{
                                 width: COURSE_PAGE_WIDTH,
                                 minHeight: 400,
                                 padding: mobileContainerWidth < 640 ? '96px' : '32px',
                                 border: '1px solid #cbd5e1',
                                 boxSizing: 'border-box',
                                 boxShadow: '0 8px 32px 0 rgba(0,0,0,0.18), 0 1.5px 4px 0 rgba(0,0,0,0.10)',
                                 zoom: coursePageFitScale,
                               }}
                             >
                               <div
                                 className="prose prose-slate max-w-none text-slate-900 leading-bold font-sans"
                                 dangerouslySetInnerHTML={{ __html: topicData.outlineContent }}
                               />
                             </div>
                           </div>
                          ) : !fullSubContentMap ? (
                           /* Skeleton while topic content loads */
                           <div data-progress-page="true" className="course-reader-page-slot relative overflow-hidden" style={{ width: COURSE_PAGE_WIDTH * coursePageFitScale, height: 300 * coursePageFitScale }}>
                             <div
                               className="absolute left-0 top-0 bg-white border border-slate-200"
                               style={{
                                 width: COURSE_PAGE_WIDTH,
                                 minHeight: 300,
                                 transform: `scale(${coursePageFitScale})`,
                                 transformOrigin: 'top left',
                                 boxShadow: '0 8px 32px 0 rgba(0,0,0,0.14)',
                               }}
                             >
                               <div className="h-full w-full animate-pulse flex flex-col gap-4 p-16">
                                 {[...Array(6)].map((_,i) => <div key={i} className={`h-3 bg-slate-100 rounded-full ${i % 3 === 2 ? 'w-3/4' : 'w-full'}`} />)}
                               </div>
                             </div>
                           </div>
                         ) : null}

                         {topic.subTopics?.map((subTopic: any, sIdx: number) => {
                           const subData = getCanonicalSubTopicContent(topic, subTopic, sIdx);
                           const subNum = `${topicNum}.${sIdx + 1}`;
                           // Skip entirely if no content uploaded for this subtopic
                           if (!subData?.referencePdfUrl && !subData?.outlineContent) return null;
                           return (
                             <React.Fragment key={subTopic.id}>
                                {/* SUB-TOPIC SEPARATOR — only shown when content exists below it */}
                                <div data-subtopic-id={subTopic.id} className="w-full flex items-center gap-4 py-3 animate-in fade-in duration-700">
                                   <div className="h-px flex-1 bg-slate-900/10 dark:bg-white/5" />
                                   <div className="flex items-center gap-2">
                                      <span className="text-[10px] font-black text-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded-full tracking-widest uppercase">{subNum}</span>
                                      <span className="text-[12px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide">{subTopic.title}</span>
                                   </div>
                                   <div className="h-px flex-1 bg-slate-900/10 dark:bg-white/5" />
                                </div>

                                {/* Actual Uploaded PDF or HTML Content */}
                                {subData.referencePdfUrl ? (
                                  <SyllabusDocumentSection
                                    file={subData.referencePdfUrl}
                                    zoom={zoom}
                                    pageFitScale={coursePageFitScale}
                                    isQuickSearchSelecting={isQuickSearchSelecting}
                                    quickSearchOverlayRef={quickSearchOverlayRef}
                                    handleQuickSearchMouseDown={handleQuickSearchMouseDown}
                                    handleQuickSearchMouseMove={handleQuickSearchMouseMove}
                                    finishQuickSearchCapture={finishQuickSearchCapture}
                                    handleQuickSearchTouchEnd={handleQuickSearchTouchEnd}
                                    isQuickSearchCapturing={isQuickSearchCapturing}
                                    cancelQuickSearchCapture={cancelQuickSearchCapture}
                                    quickSearchRect={quickSearchRect}
                                    onDocumentLoaded={() => setLoadedPdfsCount(n => n + 1)}
                                  />
                                ) : (
                                   <div data-progress-page="true" className="course-reader-page-slot w-full" style={{ maxWidth: COURSE_PAGE_WIDTH * coursePageFitScale }}>
                                     <div
                                       className="course-reader-page paper-sheet tiptap-editor-area bg-white"
                                       style={{
                                         width: COURSE_PAGE_WIDTH,
                                         minHeight: COURSE_PAGE_HEIGHT,
                                         border: '1px solid #cbd5e1',
                                         boxSizing: 'border-box',
                                         boxShadow: '0 8px 32px 0 rgba(0,0,0,0.18), 0 1.5px 4px 0 rgba(0,0,0,0.10)',
                                         zoom: coursePageFitScale,
                                       }}
                                     >
                                       <div 
                                         className="tiptap prose prose-slate max-w-none text-slate-900 leading-bold"
                               style={{ padding: mobileContainerWidth < 640 ? '96px' : '32px', minHeight: '11in' }}
                                         dangerouslySetInnerHTML={{ __html: subData.outlineContent }} 
                                       />
                                     </div>
                                   </div>
                                )}
                             </React.Fragment>
                           );
                         })}

                         {/* MARK AS COMPLETE — shown at the end of the last topic */}
                         {isLastTopic && (
                           <div className="w-full flex flex-col items-center gap-0 mt-12 mb-6">
                             {/* End-of-curriculum rule */}
                             <div className="w-full flex items-center gap-5 mb-10">
                               <div className="h-px flex-1 bg-slate-200 dark:bg-white/8" />
                               <span className="text-[9px] font-bold text-slate-400 dark:text-white/25 uppercase tracking-[0.4em]">End of curriculum</span>
                               <div className="h-px flex-1 bg-slate-200 dark:bg-white/8" />
                             </div>

                             {resumeProgressEnabled && (isCompleted ? (
                               /* ── COMPLETED CARD ── clean, single-palette, light+dark ── */
                               <motion.div
                                 initial={{ opacity: 0, y: 10 }}
                                 animate={{ opacity: 1, y: 0 }}
                                 className="w-[816px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/60 rounded-2xl overflow-hidden shadow-sm"
                               >
                                 {/* Thin accent top bar */}
                                 <div className="h-0.5 w-full bg-indigo-600" />
                                 <div className="flex flex-col items-center px-12 py-12 gap-8">
                                   {/* Icon + check */}
                                   <div className="relative">
                                     <div className="h-16 w-16 rounded-full bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-700/40 flex items-center justify-center">
                                       <CheckCircle2 className="h-7 w-7 text-indigo-600 dark:text-indigo-400" />
                                     </div>
                                   </div>
                                   {/* Copy */}
                                   <div className="text-center space-y-1.5">
                                     <p className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-[0.35em]">Curriculum completed</p>
                                     <h3 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">{subjectName || 'This Subject'}</h3>
                                     {completedAt && (
                                       <p className="text-sm text-slate-400 dark:text-slate-500">{completedAt}</p>
                                     )}
                                   </div>
                                   {/* Stats */}
                                   <div className="flex items-center gap-10 py-4 px-8 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/40 w-full justify-center">
                                     <div className="text-center">
                                       <div className="text-xl font-bold text-slate-900 dark:text-white">{fullSyllabus.topics?.length ?? 0}</div>
                                       <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-widest mt-0.5">Topics</div>
                                     </div>
                                     <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
                                     <div className="text-center">
                                       <div className="text-xl font-bold text-indigo-600 dark:text-indigo-400">100%</div>
                                       <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-widest mt-0.5">Complete</div>
                                     </div>
                                     <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
                                     <div className="text-center">
                                       <div className="text-xl font-bold text-slate-900 dark:text-white">{selectedClass || '—'}</div>
                                       <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-widest mt-0.5">Class</div>
                                     </div>
                                   </div>
                                   <button
                                     onClick={handleBack}
                                     className="px-7 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm tracking-wide transition-all active:scale-[0.98] flex items-center gap-2"
                                   >
                                     <ArrowLeft className="h-3.5 w-3.5" /> Return to Dashboard
                                   </button>
                                 </div>
                               </motion.div>
                             ) : (
                               /* ── MARK AS COMPLETE CTA ── clean, light+dark ── */
                               <motion.div
                                 initial={{ opacity: 0, y: 10 }}
                                 animate={{ opacity: 1, y: 0 }}
                                 transition={{ delay: 0.2 }}
                                 className="w-[816px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/60 rounded-2xl overflow-hidden shadow-sm"
                               >
                                 <div className="h-0.5 w-full bg-slate-200 dark:bg-slate-700/60" />
                                 <div className="flex flex-col items-center px-12 py-12 gap-7">
                                   {/* Copy */}
                                   <div className="text-center space-y-1.5">
                                     <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.35em]">You've reached the end</p>
                                     <h3 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Mark as Complete</h3>
                                     <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">
                                       {subjectName || 'This subject'}
                                       {selectedClass ? <> &middot; <span className="font-medium">{selectedClass}</span></> : null}
                                       {selectedSchool ? <> &middot; <span className="font-medium">{selectedSchool}</span></> : null}
                                     </p>
                                   </div>
                                   {/* Actions */}
                                   <div className="flex items-center gap-3">
                                     <button
                                       onClick={handleMarkComplete}
                                       disabled={isMarkingComplete}
                                       className="px-7 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold text-sm tracking-wide transition-all active:scale-[0.98] flex items-center gap-2"
                                     >
                                       {isMarkingComplete ? (
                                         <><div className="h-3.5 w-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
                                       ) : (
                                         <><CheckCircle2 className="h-4 w-4" /> Mark as Complete</>
                                       )}
                                     </button>
                                     <button
                                       onClick={handleBack}
                                       className="px-5 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white hover:border-slate-300 dark:hover:border-slate-600 font-semibold text-sm transition-all active:scale-[0.98]"
                                     >
                                       Back to Dashboard
                                     </button>
                                   </div>
                                 </div>
                               </motion.div>
                             ))}
                           </div>
                         )}
                      </React.Fragment>
                    );
                  })}
                 </div>
                </div>
                 </div>
              </motion.div>
            ) : (
              <>
                {items.length > 0 && (
                  <div className="shrink-0 z-30 bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800 px-3 sm:px-6 lg:px-8 py-2 flex items-center gap-2 overflow-x-auto no-scrollbar">
                    <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] mr-1 shrink-0">Content</span>
                    {items.map((it) => {
                      const isActive = (it.id && activeItemId === it.id) || (!activeItemId && pdfUrl === it.url && it.type === 'pdf') || (!activeItemId && htmlContent === it.content && it.type === 'outline');
                      return (
                        <button
                          key={it.id}
                          onClick={() => handleItemSwitch(it)}
                          className={`group flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all shrink-0 max-w-[180px] sm:max-w-[220px] ${
                            isActive
                              ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/30'
                              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-500/50'
                          }`}
                          title={it.title}
                        >
                          {it.type === 'pdf' ? (
                            <FileText className={`h-3.5 w-3.5 shrink-0 ${isActive ? 'text-white' : 'text-emerald-500'}`} />
                          ) : (
                            <BookOpen className={`h-3.5 w-3.5 shrink-0 ${isActive ? 'text-white' : 'text-indigo-500'}`} />
                          )}
                          <span className="truncate">{it.title || (it.type === 'pdf' ? 'PDF Document' : 'Outline')}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {!(pdfUrl || htmlContent) ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white dark:bg-slate-900 rounded-[2.5rem] m-4">
                    <p className="text-sm text-slate-400 font-medium">No pedagogical content available for this topic.</p>
                  </div>
                ) : pdfUrl ? (
            <motion.div
              key="pdf-infinite-viewer"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex flex-col h-full bg-white dark:bg-[#020617] relative overflow-hidden"
            >
              {/* READING PROGRESS BAR */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-white/5 z-[200]">
                <div 
                  className="h-full bg-indigo-500 rounded-r-full shadow-[0_0_10px_rgba(99,102,241,0.5)] transition-all duration-150 ease-out" 
                  style={{ width: `${scrollProgress}%` }}
                />
              </div>

              {/* COMPACT READER CONTROLS — right side */}
              {/* Note: zoom + fullscreen live in the header to avoid duplicate controls.
                  This pill is now ONLY a page-number indicator. */}
              <div className="absolute top-4 right-3 sm:top-6 sm:right-6 z-[100] flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-white/80 dark:bg-[#020617]/80 backdrop-blur-3xl rounded-xl border border-gray-200 dark:border-white/5 shadow-2xl max-w-[calc(100vw-1.5rem)]">
                <FileText className="h-3 w-3 text-indigo-500 shrink-0" />
                <span className="text-[9px] font-black text-slate-500 dark:text-white/40 uppercase tracking-widest whitespace-nowrap">Registry</span>
                <span className="text-[9px] font-black text-indigo-600 dark:text-indigo-400 tabular-nums">{currentPdfPage}<span className="text-slate-400 dark:text-white/20 font-medium">/{numPages}</span></span>
              </div>

              {/* PDF CANVAS INFINITE SCROLL */}
              <div 
                ref={scrollRefCallback}
                className="relative flex-1 overflow-y-auto p-3 sm:p-6 lg:p-12 custom-scrollbar space-y-6 sm:space-y-12 bg-pattern overflow-x-auto"
                onScroll={handleScroll}
                style={eyeCareStyle}
              >
                {isQuickSearchSelecting && (
                  <div
                    ref={quickSearchOverlayRef}
                    data-html2canvas-ignore="true"
                    className="absolute inset-0 z-[260] cursor-crosshair bg-slate-950/20 touch-none"
                    onMouseDown={handleQuickSearchMouseDown}
                    onMouseMove={handleQuickSearchMouseMove}
                    onMouseUp={finishQuickSearchCapture}
                    onTouchStart={handleQuickSearchMouseDown}
                    onTouchMove={handleQuickSearchMouseMove}
                    onTouchEnd={handleQuickSearchTouchEnd}
                  >
                    {/* Instruction card — fixed so always fully visible on all screen sizes */}
                    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[300] rounded-xl border-2 border-blue-400 bg-blue-950 px-5 py-3 text-center shadow-2xl max-w-[90vw] pointer-events-auto">
                      <div className="text-[11px] font-black uppercase tracking-widest text-white">
                        {isQuickSearchCapturing ? '⏳ Capturing…' : '✏️ Draw a box around the content'}
                      </div>
                      <button
                        onClick={(event) => { event.stopPropagation(); cancelQuickSearchCapture(); }}
                        onTouchStart={(event) => event.stopPropagation()}
                        onTouchEnd={(event) => { event.stopPropagation(); cancelQuickSearchCapture(); }}
                        className="mt-1.5 text-[10px] font-bold uppercase tracking-widest text-blue-300 hover:text-white border border-blue-500/40 hover:border-blue-400 px-3 py-0.5 rounded-full transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                    {quickSearchRect && (
                      <div
                        className="absolute border-2 border-blue-400 bg-blue-400/15 pointer-events-none"
                        style={{
                          left: quickSearchRect.x,
                          top: quickSearchRect.y,
                          width: quickSearchRect.width,
                          height: quickSearchRect.height,
                        }}
                      />
                    )}
                  </div>
                )}
                <div className="max-w-screen-xl mx-auto flex flex-col items-center">
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={({ numPages }) => { onDocumentLoadSuccess({ numPages }); setLoadedPdfsCount(n => n + 1); }}
                    className="flex flex-col items-center gap-16"
                    loading={
                      <div className="flex flex-col items-center justify-center p-20 space-y-6">
                        <div className="h-14 w-14 border-2 border-indigo-500/10 border-t-indigo-500 rounded-full animate-spin" />
                        <p className="text-[9px] uppercase font-black tracking-[0.2em] text-indigo-400">Rendering Workspace...</p>
                      </div>
                    }
                  >
                    {Array.from(new Array(numPages), (el, index) => (
                      <motion.div
                        key={`page_${index + 1}`}
                        initial={{ opacity: 0, scale: 0.98 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        className="document-page-container relative group/page"
                        data-page-num={index + 1}
                      >
                         <div className="absolute -left-16 top-0 h-full flex flex-col items-center py-6 opacity-20 group-hover/page:opacity-100 transition-opacity duration-500">
                            <span className="text-[8px] font-black text-white/40 rotate-90 origin-left uppercase tracking-[0.3em] whitespace-nowrap">PG. {String(index + 1).padStart(2, '0')} — REF</span>
                         </div>
                         <Page 
                           pageNumber={index + 1} 
                           scale={zoom}
                           className="shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)] ring-1 ring-white/10"
                           renderAnnotationLayer={false}
                           renderTextLayer={false}
                           loading={<div className="h-[1000px] w-[700px] bg-white/[0.02] animate-pulse rounded-sm" />}
                         />
                      </motion.div>
                    ))}
                  </Document>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div 
              key="text-reader"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex flex-col h-full bg-white dark:bg-[#020617] relative overflow-hidden"
            >
              {/* READING PROGRESS BAR */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-white/5 z-[200]">
                <div 
                  className="h-full bg-indigo-500 rounded-r-full shadow-[0_0_10px_rgba(99,102,241,0.5)] transition-all duration-150 ease-out" 
                  style={{ width: `${scrollProgress}%` }}
                />
              </div>

              {/* READER CONTROLS FOR TEXT — right side */}
              {/* Note: zoom + fullscreen live in the header to avoid duplicate controls.
                  This pill is now ONLY a mode label (Doc Mode • Digital Reader). */}
              <div className="absolute top-4 right-3 sm:top-6 sm:right-6 z-[100] flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-white/80 dark:bg-[#020617]/80 backdrop-blur-3xl rounded-xl border border-gray-200 dark:border-white/5 shadow-2xl max-w-[calc(100vw-1.5rem)]">
                <BookOpen className="h-3 w-3 text-indigo-500 shrink-0" />
                <span className="text-[9px] font-black text-slate-500 dark:text-white/40 uppercase tracking-widest whitespace-nowrap">Doc Mode</span>
                <span className="text-[9px] font-black text-indigo-500 dark:text-indigo-400 whitespace-nowrap">Digital <span className="text-slate-400 dark:text-white/20">Reader</span></span>
              </div>

              {/* TEXT CONTENT PAPER CANVAS */}
              <div 
                ref={scrollRefCallback}
                className="relative flex-1 overflow-y-auto p-3 sm:p-6 lg:p-12 custom-scrollbar space-y-6 sm:space-y-12 bg-[#adb5bd] dark:bg-[#0f172a] overflow-x-auto"
                onScroll={handleScroll}
                style={eyeCareStyle}
              >
                {isQuickSearchSelecting && (
                  <div
                    ref={quickSearchOverlayRef}
                    data-html2canvas-ignore="true"
                    className="absolute inset-0 z-[260] cursor-crosshair bg-slate-950/20 touch-none"
                    onMouseDown={handleQuickSearchMouseDown}
                    onMouseMove={handleQuickSearchMouseMove}
                    onMouseUp={finishQuickSearchCapture}
                    onTouchStart={handleQuickSearchMouseDown}
                    onTouchMove={handleQuickSearchMouseMove}
                    onTouchEnd={handleQuickSearchTouchEnd}
                  >
                    {/* Instruction card — fixed so always fully visible on all screen sizes */}
                    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[300] rounded-xl border-2 border-blue-400 bg-blue-950 px-5 py-3 text-center shadow-2xl max-w-[90vw] pointer-events-auto">
                      <div className="text-[11px] font-black uppercase tracking-widest text-white">
                        {isQuickSearchCapturing ? '⏳ Capturing…' : '✏️ Draw a box around the content'}
                      </div>
                      <button
                        onClick={(event) => { event.stopPropagation(); cancelQuickSearchCapture(); }}
                        onTouchStart={(event) => event.stopPropagation()}
                        onTouchEnd={(event) => { event.stopPropagation(); cancelQuickSearchCapture(); }}
                        className="mt-1.5 text-[10px] font-bold uppercase tracking-widest text-blue-300 hover:text-white border border-blue-500/40 hover:border-blue-400 px-3 py-0.5 rounded-full transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                    {quickSearchRect && (
                      <div
                        className="absolute border-2 border-blue-400 bg-blue-400/15 pointer-events-none"
                        style={{
                          left: quickSearchRect.x,
                          top: quickSearchRect.y,
                          width: quickSearchRect.width,
                          height: quickSearchRect.height,
                        }}
                      />
                    )}
                  </div>
                )}
                <div 
                  className="flex flex-col items-center pt-4 sm:pt-8 pb-24 sm:pb-32 w-full" 
                  style={{ transform: `scale(${zoom * mobileFitScale})`, transformOrigin: 'top center', transition: 'transform 0.2s cubic-bezier(0.2, 0, 0, 1)' }}
                >
                  <div 
                    className="paper-sheet relative bg-white shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)] w-full sm:w-[816px] overflow-x-auto"
                    style={{ minHeight: '1056px', border: '1px solid #cbd5e1', boxSizing: 'border-box', padding: '12px 12px 24px 12px' }}
                  >
                    <div
                      className="prose prose-slate max-w-none text-slate-900 leading-relaxed overflow-hidden"
                      style={{ color: '#0f172a', fontSize: '16px', lineHeight: 1.7 }}
                      dangerouslySetInnerHTML={{ __html: htmlContent || '<p class="text-slate-400 italic">No content available.</p>' }}
                    />
                    {htmlContent && !htmlTextOnly && !htmlHasNonTextContent && (
                      <div className="mt-4 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 text-amber-700 dark:text-amber-300 text-xs">
                        <strong>Empty content detected.</strong> The HTML is loaded from the route state but contains no visible text.
                        This usually means the writer saved an empty document. Open this sub-topic in the writer to add content.
                      </div>
                    )}
                  </div>
                </div>
              </div>{/* end relative restore-overlay wrapper */}
             </motion.div>
          )}
              </>
          )}
        </AnimatePresence>
      </div>

        {/* Right Pane (Samsung Notes Side Editor) — full-screen overlay on mobile */}
        {showNoteTakerSplit && (
          <div 
            className={cn(
              "h-full bg-white dark:bg-[#080b12] border-l border-slate-200 dark:border-white/10 flex flex-col shadow-2xl relative transition-all duration-300 text-slate-950 dark:text-white",
              // Mobile: full-screen fixed overlay
              mobileContainerWidth < 768 && "fixed inset-0 z-[400]",
              // Desktop: side panel
              mobileContainerWidth >= 768 && "z-[300]",
              mobileContainerWidth >= 768 && (noteTakerFullscreen ? "flex-1" : (notePaneWidth === 'wide' ? "w-[50%]" : "w-[40%]"))
            )}
            style={{ overscrollBehavior: 'contain' }}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onMouseDown={(e) => {
              const target = e.target as HTMLElement;
              if (target.closest('[data-note-toolbar="true"]') || (editorRef.current && editorRef.current.contains(target))) return;
              clearNoteSelection();
            }}
          >
            {/* Top Bar matching Samsung Notes */}
            <div className="min-h-14 border-b border-slate-200 dark:border-emerald-400/10 px-3 py-2 flex flex-wrap items-center justify-between gap-2 bg-slate-50 dark:bg-[#10151f] shrink-0 text-slate-600 dark:text-slate-300 shadow-sm dark:shadow-[0_1px_0_rgba(255,255,255,0.04)]">
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <button 
                  onClick={() => setShowNoteTakerSplit(false)}
                  className={cn(
                    "hover:bg-slate-200 dark:hover:bg-white/10 rounded-lg transition-colors flex items-center text-slate-600 dark:text-slate-300 hover:text-slate-950 dark:hover:text-white shrink-0",
                    // Mobile: larger touch target
                    mobileContainerWidth < 768 ? "h-10 px-3 gap-2" : "h-8 px-1.5"
                  )}
                  title="Close Notes"
                >
                  <X className="h-5 w-5 text-emerald-500" />
                  <span className="text-[9px] font-black uppercase tracking-wider text-slate-600 dark:text-slate-300">Close Notes</span>
                </button>
                <div className="hidden sm:block h-4 w-px bg-slate-200 dark:bg-white/10" />
                <div className="flex bg-slate-200 dark:bg-white/10 rounded overflow-hidden p-0.5">
                  <button
                    onClick={() => {
                      flushActiveTextNote();
                      setNoteScope('page');
                    }}
                    className={cn(
                      "text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded-sm transition-all whitespace-nowrap",
                      noteScope === 'page' ? "bg-white dark:bg-slate-800 shadow-sm text-emerald-600 dark:text-emerald-400" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                    )}
                  >
                    Pg {pdfUrl ? currentPdfPage : currentPage || 1}
                  </button>
                  <button
                    onClick={() => {
                      flushActiveTextNote();
                      setNoteScope('subject');
                    }}
                    className={cn(
                      "text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded-sm transition-all whitespace-nowrap",
                      noteScope === 'subject' ? "bg-white dark:bg-slate-800 shadow-sm text-emerald-600 dark:text-emerald-400" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                    )}
                  >
                    Subject Wide
                  </button>
                </div>
                
                <div className="flex bg-slate-200 dark:bg-white/10 rounded overflow-hidden p-0.5">
                  <button
                    onClick={() => setNoteMode('text')}
                    className={cn(
                      "text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded-sm transition-all whitespace-nowrap",
                      noteMode === 'text' ? "bg-white dark:bg-slate-800 shadow-sm text-emerald-600 dark:text-emerald-400" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                    )}
                  >
                    Text Note
                  </button>
                  <button
                    onClick={() => {
                      flushActiveTextNote();
                      setNoteMode('photo');
                      setIsEditingPhotoNotes(false);
                      debugPhotoNotes('photo mode tab clicked', {
                        activeNoteKey,
                        activePhotoKey: getPhotoNoteKey(activeNoteKey),
                      });
                    }}
                    className={cn(
                      "text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded-sm transition-all whitespace-nowrap",
                      noteMode === 'photo' ? "bg-white dark:bg-slate-800 shadow-sm text-emerald-600 dark:text-emerald-400" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                    )}
                  >
                    Photos
                  </button>
                </div>
              </div>

              <div className={cn(
                "flex shrink-0 flex-wrap items-center justify-end gap-1.5 text-[9px] font-bold uppercase tracking-wider",
                // Mobile: hide fullscreen/expand controls (already full-screen)
                mobileContainerWidth < 768 && "hidden"
              )}>
                {/* Fullscreen toggle — occupies entire content area */}
                <button
                  onClick={() => setNoteTakerFullscreen(v => !v)}
                  title={noteTakerFullscreen ? 'Exit Fullscreen' : 'Fullscreen Notes'}
                  className="h-8 px-2 hover:bg-slate-200 dark:hover:bg-white/10 rounded transition-colors text-slate-500 dark:text-slate-400 hover:text-slate-950 dark:hover:text-white flex items-center gap-1 whitespace-nowrap"
                >
                  {noteTakerFullscreen
                    ? <><Minimize2 className="h-3 w-3 text-emerald-400" /><span>Exit Fullscreen</span></>
                    : <><Maximize2 className="h-3 w-3" /><span>Fullscreen</span></>}
                </button>
                {!noteTakerFullscreen && (
                  <button
                    onClick={toggleNotePaneWidth}
                    className="h-8 px-2 hover:bg-slate-200 dark:hover:bg-white/10 rounded transition-colors text-slate-500 dark:text-slate-400 hover:text-slate-950 dark:hover:text-white whitespace-nowrap"
                  >
                    {notePaneWidth === 'wide' ? 'Side-by-side' : 'Expand'}
                  </button>
                )}
                <button
                  onClick={() => setShowNotesIndex(v => !v)}
                  className={cn(
                    "h-8 px-2 rounded transition-colors whitespace-nowrap",
                    showNotesIndex
                      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300"
                      : "hover:bg-slate-200 dark:hover:bg-white/10 text-slate-500 dark:text-slate-400 hover:text-slate-950 dark:hover:text-white"
                  )}
                >
                  View Notes
                </button>
                <button
                  onClick={() => triggerImageUpload()}
                  disabled={isUploadingImage}
                  className="h-8 px-2 hover:bg-slate-200 dark:hover:bg-white/10 rounded transition-colors text-slate-500 dark:text-slate-400 hover:text-slate-950 dark:hover:text-white flex items-center gap-1 whitespace-nowrap"
                  title="Upload Photo Note"
                >
                  <Camera className="h-3.5 w-3.5 text-emerald-400" />
                  <span>Photo</span>
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="sr-only"
                />
                <button
                  onClick={() => {
                    if (noteMode === 'photo') {
                      setIsEditingPhotoNotes(false);
                      return;
                    }
                    if (editorRef.current && !isPhotoNoteContent(notesRef.current[activeNoteKey])) {
                      handleSaveNote(activeNoteKey, editorRef.current.innerHTML);
                    }
                  }}
                  disabled={isSavingNotes || isUploadingImage}
                  className="h-8 px-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-600/50 rounded text-white font-black transition-colors whitespace-nowrap"
                >
                  {isSavingNotes ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            {showNotesIndex && (
              <div className="border-b border-slate-200 dark:border-emerald-400/10 bg-white dark:bg-[#0b1019] px-4 py-3 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Notes index</p>
                    <p className="text-[9px] font-semibold text-slate-400 dark:text-white/30">{notesIndexEntries.length} saved page{notesIndexEntries.length === 1 ? '' : 's'}</p>
                  </div>
                  <button
                    onClick={() => setShowNotesIndex(false)}
                    className="text-[9px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-700 dark:hover:text-white"
                  >
                    Hide
                  </button>
                </div>
                <div className="max-h-36 overflow-y-auto custom-scrollbar space-y-1.5 pr-1">
                  {notesIndexEntries.length === 0 ? (
                    <div className="rounded-md border border-dashed border-slate-200 dark:border-white/10 px-3 py-3 text-[10px] font-semibold text-slate-400 dark:text-white/30">
                      No saved notes yet.
                    </div>
                  ) : notesIndexEntries.map(entry => (
                    <button
                      key={entry.key}
                      onClick={() => jumpToNotePage(entry.key)}
                      className={cn(
                        "w-full flex items-start gap-3 rounded-md border px-3 py-2 text-left transition-colors",
                        entry.key === activeNoteKey
                          ? "border-emerald-400/40 bg-emerald-500/10"
                          : "border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5"
                      )}
                    >
                      <span className={cn(
                        "shrink-0 rounded px-2 py-0.5 text-[9px] font-black",
                        entry.isSubject ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-300" : "bg-slate-100 dark:bg-white/8 text-emerald-600 dark:text-emerald-300"
                      )}>
                        {entry.isSubject ? 'Subject' : `Pg ${entry.page}`}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-[10px] font-bold text-slate-700 dark:text-slate-200">
                          {entry.preview || (entry.hasPhotos ? 'Photo notes only' : 'Empty note')}
                        </span>
                        <span className="block text-[9px] text-slate-400 dark:text-white/30">
                          {[
                            entry.preview ? `${entry.preview.length} chars` : null,
                            entry.hasPhotos ? `${entry.photoCount} photo(s)` : null
                          ].filter(Boolean).join(' • ')}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Note Body: Canvas + Vertical Toolbar */}
              <div className="flex-1 flex overflow-hidden relative dark:bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.10),transparent_34%),linear-gradient(180deg,#080b12,#05070d)]">
                {/* Note Content Sheet */}
              <div key={`${activeNoteKey}-${noteMode}`} className="flex-1 flex flex-col bg-white dark:bg-[#05070d] overflow-hidden p-6">
                <div className="border-b border-slate-200 dark:border-emerald-400/10 pb-2 mb-4">
                  <input
                    type="text"
                    placeholder="Title"
                    defaultValue={`${subjectName || 'Lesson'} Notes - ${noteScope === 'subject' ? 'Subject Wide' : `Page ${pdfUrl ? currentPdfPage : currentPage || 1}`}`}
                    className="w-full bg-transparent text-xl font-bold border-none outline-none focus:ring-0 text-slate-950 dark:text-white placeholder-slate-400 dark:placeholder-white/20"
                  />
                  <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-wider mt-1">
                    {noteScope === 'subject' ? '📌 These notes apply to the entire subject.' : `📌 These notes are tied to Page ${pdfUrl ? currentPdfPage : currentPage || 1}`}
                  </p>
                </div>
                
                {isUploadingImage ? (
                  <div className="flex-1 flex flex-col items-center justify-center gap-3 bg-slate-50 dark:bg-zinc-900 rounded-lg border border-slate-200 dark:border-white/5 p-6">
                    <Loader2 className="h-8 w-8 text-emerald-500 animate-spin" />
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400">Uploading photo...</span>
                  </div>
                ) : noteMode === 'photo' ? (
                  <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col gap-4 p-2">
                    {(() => {
                      // Support both separated keys and legacy keys
                      const photoContent = getPhotoNoteContent(activeNoteKey, notes);
                      const photoUrls = photoContent.replace(/^images?:/, '').split('||').filter(Boolean);
                      
                      if (photoUrls.length === 0) {
                        return (
                          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-slate-400 dark:text-white/30">
                            <Camera className="h-8 w-8 opacity-50" />
                            <span className="text-[10px] font-black uppercase tracking-wider">No photos uploaded for this {noteScope}</span>
                            <button
                              onClick={() => triggerImageUpload()}
                              className="mt-2 px-4 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all"
                            >
                              Upload Photo
                            </button>
                          </div>
                        );
                      }
                      
                      return (
                        <>
                          <div className="flex items-center justify-end gap-2 shrink-0">
                            {isEditingPhotoNotes ? (
                              <>
                                <button
                                  onClick={() => triggerImageUpload()}
                                  className="px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-md text-[10px] font-black uppercase tracking-wider transition-all"
                                >
                                  Add Photo
                                </button>
                                <button
                                  onClick={() => triggerImageUpload({ replace: true })}
                                  className="px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:text-blue-300 rounded-md text-[10px] font-black uppercase tracking-wider transition-all"
                                >
                                  Replace
                                </button>
                                <button
                                  onClick={() => setIsEditingPhotoNotes(false)}
                                  className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-white/10 dark:hover:bg-white/15 text-slate-600 dark:text-slate-200 rounded-md text-[10px] font-black uppercase tracking-wider transition-all"
                                >
                                  Done
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => setIsEditingPhotoNotes(true)}
                                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-white/10 dark:hover:bg-white/15 text-slate-600 dark:text-slate-200 rounded-md text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-1.5"
                              >
                                <Edit className="h-3 w-3" />
                                Edit
                              </button>
                            )}
                          </div>

                          {photoUrls.map((url, i, arr) => (
                            <div key={`${url}-${i}`} className="w-full relative flex flex-col gap-2 bg-slate-50 dark:bg-zinc-900 rounded-lg overflow-hidden border border-slate-200 dark:border-white/5 p-2 shrink-0">
                              <img
                                src={url}
                                loading="lazy"
                                alt={`Scribbled notes ${i + 1}`}
                                className="w-full h-auto object-contain"
                              />
                              {isEditingPhotoNotes && (
                                <button
                                  onClick={async () => {
                                    if (window.confirm('Are you sure you want to remove this photo?')) {
                                      const newArr = arr.filter((_, index) => index !== i);
                                      const newContent = newArr.length > 0 ? `images:${newArr.join('||')}` : '';
                                      const targetKey = notes[getPhotoNoteKey(activeNoteKey)] ? getPhotoNoteKey(activeNoteKey) : activeNoteKey;
                                      setNotes(prev => ({ ...prev, [targetKey]: newContent }));
                                      await handleSaveNote(targetKey, newContent);
                                      if (newArr.length === 0) setIsEditingPhotoNotes(false);
                                    }
                                  }}
                                  className="w-full px-4 py-2 bg-red-600/10 hover:bg-red-600/20 text-red-600 dark:text-red-400 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5"
                                >
                                  <Trash2 className="h-3 w-3" />
                                  Delete Photo
                                </button>
                              )}
                            </div>
                          ))}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={() => {
                      if (editorRef.current) {
                        const htmlVal = editorRef.current.innerHTML;
                        setNotes(prev => ({ ...prev, [activeNoteKey]: htmlVal }));
                        window.setTimeout(updateNoteFormatState, 0);
                      }
                    }}
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox') {
                        const cb = target as HTMLInputElement;
                        if (cb.checked) {
                          cb.setAttribute('checked', 'checked');
                        } else {
                          cb.removeAttribute('checked');
                        }
                        if (editorRef.current) {
                          const htmlVal = editorRef.current.innerHTML;
                          setNotes(prev => ({ ...prev, [activeNoteKey]: htmlVal }));
                        }
                      }
                    }}
                    onMouseUp={saveSelection}
                    onKeyUp={saveSelection}
                    onKeyDown={handleNoteKeyDown}
                    onPaste={handleNotePaste}
                    onFocus={updateNoteFormatState}
                    onBlur={saveSelection}
                    className="flex-1 text-slate-950 dark:text-white outline-none overflow-y-auto custom-scrollbar leading-relaxed text-sm prose prose-slate dark:prose-invert max-w-none prose-sm font-medium note-editor-content select-text"
                    style={{ minHeight: '100px', overscrollBehavior: 'contain' }}
                    onWheel={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                    {...{ placeholder: "Start writing notes here..." }}
                  />
                )}
              </div>

              {/* Samsung Notes style vertical formatting toolbar */}
              {noteMode === 'text' && !isUploadingImage && (
                <div data-note-toolbar="true" className="w-16 bg-slate-50 dark:bg-[#111827] border-l border-slate-200 dark:border-emerald-400/10 flex flex-col items-center py-4 gap-4 overflow-y-auto shrink-0 select-none shadow-[-8px_0_24px_rgba(0,0,0,0.18)]">
                {/* Font Size Selection */}
                <div className="flex flex-col items-center gap-1">
                  <span className="text-[8px] text-slate-400 dark:text-white/40 font-black uppercase">Size</span>
                  <select
                    onChange={(e) => {
                      execEditorCommand('fontSize', e.target.value);
                    }}
                    value={activeNoteFontSize}
                    title="Font size"
                    aria-label="Font size"
                    className="bg-white/90 dark:bg-[#23272e] text-slate-900 dark:text-slate-100 text-[10px] rounded-md px-2 py-1 border border-slate-200 dark:border-slate-700 shadow-sm dark:shadow-none focus:outline-none focus:ring-2 focus:ring-blue-400 dark:focus:ring-emerald-400/60 hover:border-blue-300 dark:hover:border-emerald-400/60 transition-colors duration-150"
                  >
                    <option value="2">13</option>
                    <option value="3">16</option>
                    <option value="4">18</option>
                    <option value="5">24</option>
                    <option value="6">32</option>
                  </select>
                </div>

                <div className="h-px w-8 bg-slate-200 dark:bg-white/10" />

                {/* Bold, Italic, Underline, Strike */}
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onMouseDown={(e) => runNoteToolbarCommand(e, 'bold')}
                    className={noteToolButtonClass(activeNoteFormats.bold, 'font-bold')}
                    title="Bold"
                    aria-pressed={activeNoteFormats.bold ? 'true' : 'false'}
                  >
                    B
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => runNoteToolbarCommand(e, 'italic')}
                    className={noteToolButtonClass(activeNoteFormats.italic, 'italic')}
                    title="Italic"
                    aria-pressed={activeNoteFormats.italic ? 'true' : 'false'}
                  >
                    I
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => runNoteToolbarCommand(e, 'underline')}
                    className={noteToolButtonClass(activeNoteFormats.underline, 'underline')}
                    title="Underline"
                    aria-pressed={activeNoteFormats.underline ? 'true' : 'false'}
                  >
                    U
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => runNoteToolbarCommand(e, 'strikeThrough')}
                    className={noteToolButtonClass(activeNoteFormats.strikeThrough, 'line-through')}
                    title="Strike"
                    aria-pressed={activeNoteFormats.strikeThrough ? 'true' : 'false'}
                  >
                    T
                  </button>
                </div>

                <div className="h-px w-8 bg-slate-200 dark:bg-white/10" />

                {/* Colors */}
                <div className="flex flex-col items-center gap-1.5">
                  <span className="text-[8px] text-slate-400 dark:text-white/40 font-black uppercase">Color</span>
                  <div className="grid grid-cols-2 gap-1 px-2">
                    {['#111827', '#ffffff', '#ef4444', '#f97316', '#facc15', '#22c55e', '#3b82f6', '#a855f7'].map(color => (
                      <button
                        key={color}
                        onMouseDown={(e) => runNoteToolbarCommand(e, 'foreColor', color)}
                        className={cn(
                          "h-3 w-3 rounded-full border transition-transform active:scale-90",
                          activeNoteTextColor === normalizeCssColor(color)
                            ? "border-blue-500 ring-2 ring-blue-500/40 scale-110"
                            : "border-slate-300 dark:border-white/20"
                        )}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>

                {/* Highlights */}
                <div className="flex flex-col items-center gap-1.5">
                  <span className="text-[8px] text-slate-400 dark:text-white/40 font-black uppercase">Bg</span>
                  <div className="grid grid-cols-2 gap-1 px-2">
                    {['transparent', 'rgba(239,68,68,0.35)', 'rgba(249,115,22,0.35)', 'rgba(250,204,21,0.35)', 'rgba(34,197,94,0.35)', 'rgba(59,130,246,0.35)', 'rgba(168,85,247,0.35)', 'rgba(255,255,255,0.2)'].map(color => (
                      <button
                        key={color}
                        onMouseDown={(e) => runNoteToolbarCommand(e, 'backColor', color)}
                        className={cn(
                          "h-3 w-3 rounded-full border transition-transform active:scale-90 flex items-center justify-center overflow-hidden",
                          activeNoteHighlightColor === normalizeCssColor(color)
                            ? "border-blue-500 ring-2 ring-blue-500/40 scale-110"
                            : "border-slate-300 dark:border-white/20"
                        )}
                        style={{ backgroundColor: color === 'transparent' ? 'var(--note-transparent-swatch)' : color }}
                        title={color === 'transparent' ? 'No Highlight' : color}
                      >
                        {color === 'transparent' && <span className="text-[6px] text-slate-400 dark:text-white/30 font-bold">X</span>}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="h-px w-8 bg-slate-200 dark:bg-white/10" />

                {/* Lists & Alignment */}
                <div className="flex flex-col gap-2">
                  <button
                    onMouseDown={(e) => runNoteToolbarCommand(e, 'insertHTML', '<input type="checkbox" style="margin-right: 6px; width: 12px; height: 12px;" />&nbsp;')}
                    className={noteToolButtonClass(false)}
                    title="Insert Checkbox"
                  >
                    ☑
                  </button>
                  <button
                    onMouseDown={(e) => runNoteToolbarCommand(e, 'insertUnorderedList')}
                    className={noteToolButtonClass(activeNoteFormats.insertUnorderedList)}
                    title="Bullet List"
                    aria-pressed={activeNoteFormats.insertUnorderedList ? 'true' : 'false'}
                  >
                    •=
                  </button>
                  <button
                    onMouseDown={(e) => runNoteToolbarCommand(e, 'insertOrderedList')}
                    className={noteToolButtonClass(activeNoteFormats.insertOrderedList)}
                    title="Numbered List"
                    aria-pressed={activeNoteFormats.insertOrderedList ? 'true' : 'false'}
                  >
                    1=
                  </button>
                </div>

                <div className="h-px w-8 bg-slate-200 dark:bg-white/10" />

                <div className="flex flex-col gap-2">
                  <button
                    onMouseDown={(e) => runNoteToolbarCommand(e, 'justifyLeft')}
                    className={noteToolButtonClass(activeNoteFormats.justifyLeft)}
                    title="Align Left"
                    aria-pressed={activeNoteFormats.justifyLeft ? 'true' : 'false'}
                  >
                    L
                  </button>
                  <button
                    onMouseDown={(e) => runNoteToolbarCommand(e, 'justifyCenter')}
                    className={noteToolButtonClass(activeNoteFormats.justifyCenter)}
                    title="Align Center"
                    aria-pressed={activeNoteFormats.justifyCenter ? 'true' : 'false'}
                  >
                    C
                  </button>
                  <button
                    onMouseDown={(e) => runNoteToolbarCommand(e, 'justifyRight')}
                    className={noteToolButtonClass(activeNoteFormats.justifyRight)}
                    title="Align Right"
                    aria-pressed={activeNoteFormats.justifyRight ? 'true' : 'false'}
                  >
                    R
                  </button>
                </div>
              </div>
            )}
            </div>
          </div>
        )}
      </div>
      )}

      <style>{`
        :root {
          --note-transparent-swatch: #f8fafc;
        }
        .dark {
          --note-transparent-swatch: #2d2d2d;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.1);
        }
        .no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        @media (min-width: 640px) {
          .paper-sheet { padding: 64px !important; }
        }
        @media (min-width: 1024px) {
          .paper-sheet { padding: 96px !important; }
        }
        .react-pdf__Page__canvas {
          margin: 0 auto;
          max-width: 100% !important;
          height: auto !important;
        }
        .reader-scroll-area {
          --course-reader-side-gap: 64px;
        }
        .course-reader-page-slot {
          overflow: visible;
        }
        .course-reader-page {
          width: 816px;
          max-width: none !important;
          overflow: hidden;
          transform-origin: top center;
        }
        .course-reader-page .react-pdf__Page,
        .course-reader-page .react-pdf__Page__canvas {
          width: 816px !important;
          max-width: none !important;
        }
        .tiptap-editor-area .tiptap { outline: none; min-height: 11in; line-height: 1.6; font-size: 16px; color: #1a1a1a; word-break: break-word; }
        .tiptap-editor-area .tiptap h1 { font-size: 32px; line-height: 1.3; border-bottom: 2px solid #eee; margin-bottom: 16px; padding-bottom: 8px; color: #000; font-weight: 800; }
        .tiptap-editor-area .tiptap h2 { font-size: 24px; line-height: 1.3; margin-top: 24px; margin-bottom: 12px; color: #333; font-weight: 700; }
        .tiptap-editor-area .tiptap h3 { font-size: 20px; line-height: 1.4; margin-top: 20px; margin-bottom: 8px; color: #444; font-weight: 600; }
        .tiptap-editor-area .tiptap p { margin: 4px 0; min-height: 24px; }
        .tiptap-editor-area .tiptap ul { list-style-type: disc; padding-left: 24px; margin: 8px 0; }
        .tiptap-editor-area .tiptap ol { list-style-type: decimal; padding-left: 24px; margin: 8px 0; }
        .tiptap-editor-area .tiptap li { margin: 4px 0; }
        .tiptap-editor-area .tiptap table { border-collapse: collapse; table-layout: fixed; width: 100%; margin: 12px 0; }
        .tiptap-editor-area .tiptap table td, .tiptap-editor-area .tiptap table th { min-width: 50px; border: 1px solid #cbd5e1; padding: 8px 12px; vertical-align: top; position: relative; }
        .tiptap-editor-area .tiptap table th { font-weight: 700; background-color: #f8fafc; }

        /* Notes Side Editor Content Styles to ensure formatting controls are functional */
        .note-editor-content {
          min-height: 100px;
          caret-color: #60a5fa;
          border-radius: 10px;
          padding: 10px 12px;
          transition: background-color 160ms ease, box-shadow 160ms ease;
        }
        .dark .note-editor-content {
          background: rgba(15, 23, 42, 0.36);
          box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.08);
        }
        .dark .note-editor-content:focus {
          background: rgba(15, 23, 42, 0.52);
          box-shadow: inset 0 0 0 1px rgba(16, 185, 129, 0.18), 0 0 28px rgba(16, 185, 129, 0.06);
        }
        .note-editor-content::selection,
        .note-editor-content *::selection {
          background: #0a84ff !important;
          color: #ffffff !important;
        }
        .note-editor-content::-moz-selection,
        .note-editor-content *::-moz-selection {
          background: #0a84ff !important;
          color: #ffffff !important;
        }
        .note-editor-content:empty::before {
          content: attr(placeholder);
          color: rgb(148, 163, 184);
          pointer-events: none;
          display: block;
        }
        .dark .note-editor-content:empty::before {
          color: rgba(255, 255, 255, 0.3);
        }
        .note-editor-content b, .note-editor-content strong {
          font-weight: bold !important;
        }
        .note-editor-content i, .note-editor-content em {
          font-style: italic !important;
        }
        .note-editor-content u {
          text-decoration: underline !important;
        }
        .note-editor-content strike, .note-editor-content s {
          text-decoration: line-through !important;
        }
        .note-editor-content ul {
          list-style-type: disc !important;
          padding-left: 20px !important;
          margin: 8px 0 !important;
        }
        .note-editor-content ol {
          list-style-type: decimal !important;
          padding-left: 20px !important;
          margin: 8px 0 !important;
        }
        .note-editor-content li {
          display: list-item !important;
          margin: 3px 0;
        }
        .note-editor-content input[type="checkbox"] {
          cursor: pointer;
          accent-color: #10b981;
        }
        .note-editor-content font[size="2"] { font-size: 13px !important; }
        .note-editor-content font[size="3"] { font-size: 16px !important; }
        .note-editor-content font[size="4"] { font-size: 18px !important; }
        .note-editor-content font[size="5"] { font-size: 24px !important; }
        .note-editor-content font[size="6"] { font-size: 32px !important; }
      `}</style>
      {/* Debug Modal */}
      {showDebugModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-[#020617] rounded-xl shadow-2xl w-full max-w-3xl border border-slate-200 dark:border-white/10 flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between p-4 border-b border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-900/50">
              <div className="flex items-center gap-2 text-rose-600 dark:text-rose-400">
                <Bug className="h-5 w-5" />
                <h2 className="font-bold">Scroll Calculation Debug Data</h2>
              </div>
              <button onClick={() => setShowDebugModal(false)} title="Close" aria-label="Close" className="text-slate-400 hover:text-slate-600 dark:hover:text-white p-2 transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 bg-slate-900 text-green-400 font-mono text-xs text-left leading-relaxed">
              <pre>{debugData}</pre>
            </div>
            <div className="p-4 border-t border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-900/50 flex justify-end gap-3">
              <button 
                onClick={() => setShowDebugModal(false)}
                className="px-4 py-2 rounded-lg font-bold text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
              >
                Close
              </button>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(debugData);
                  toast.success("Debug data copied!");
                }}
                className="px-4 py-2 rounded-lg font-bold text-sm bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg transition-colors flex items-center gap-2"
              >
                Copy to Clipboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubTopicContentViewer;
